import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import { TEMPLATE_PRESETS } from '../data/templates';
import { ActiveTemplate } from '../types';
import { getStoredToken, postLog } from '../utils/authApi';
import { getCurrencyDisplay, getCurrencySymbol, CURRENCY_MAP } from '../utils/currency';
import { getCountryCurrency } from '../utils/countryCurrency';
import { extractDynamicFields, DynamicField } from './extractDynamicFields';
import { autoMapColumns, FieldToMap } from '../utils/fuzzyMatch';
import { PAGE_CONFIGS, buildCwElements, renderCwToHtml, getPageCss } from './QuickInvoiceView';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import { inspectTaxId } from '../utils/taxId';
import { getSchemesForCountry, getDefaultSchemeForCountryAndRegion, getSchemeById as getSchemeByIdBase, computeTaxBreakdown, TaxScheme, buildCustomScheme, CustomTaxSchemeConfig } from '../utils/taxSchemes';
import { getOrderedCountryList } from '../utils/countryNames';
import StatePicker from './StatePicker';
import { amountToWords } from '../utils/numberToWords';

// Master set of countries that have regional states/provinces (matches Product Catalog)
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import * as chrono from 'chrono-node';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export type TaxCalculationMode = 'exact_file' | 'file_amounts_country_name' | 'auto_calculate';

// Base fields every template needs, regardless of what dynamic fields it detects.
const BASE_FIELDS: FieldToMap[] = [
  { key: 'group_id', label: 'Invoice # / Order ID (groups multi-items)' },
  { key: 'client_name', label: 'Client / Recipient Name' },
  { key: 'client_tax_id', label: 'Buyer GSTIN / Tax ID / VAT ID (Auto-detects B2B)' },
  { key: 'client_region', label: 'Buyer State / Province (e.g. Karnataka / NY)' },
  { key: 'client_email', label: 'Email' },
  { key: 'client_phone', label: 'Phone' },
  { key: 'client_address', label: 'Address' },
  { key: 'description', label: 'Item Description' },
  { key: 'qty', label: 'Quantity' },
  { key: 'rate', label: 'Rate / Price' },
  { key: 'tax_rate', label: 'Tax % (e.g. 18% or 5)' },
  { key: 'tax_name', label: 'Tax Name / Label in File (e.g. VAT, Sales Tax)' },
  { key: 'tax_amount', label: 'Exact Tax Amount (optional from file)' },
  { key: 'subtotal', label: 'Exact Subtotal (optional from file)' },
  { key: 'total_amount', label: 'Exact Total / Grand Total (optional from file)' },
  { key: 'b2b_tax_rate', label: 'Special B2B Tax % (optional)' },
  { key: 'tax_inclusive', label: 'Tax Inclusive (Yes/No)' },
  { key: 'invoice_date', label: 'Date' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'po_number', label: 'PO # / Purchase Order / Reference' },
  { key: 'notes', label: 'Notes' },
];

const REQUIRED_FIELDS = new Set<string>([]); // client_name no longer mandatory — many templates (labels, stickers) have no recipient at all

// Fields where, if mapped but the cell is blank, we auto-generate a fallback
// value instead of failing the row — this is what makes "even if they don't
// give a tracking number" work: it becomes optional, not a blocker.
const AUTO_FALLBACK_FIELD_PATTERN = /track|awb|consign|waybill|serial|ref|order/i;

// Tries to parse ANY date/time string format and return clean parts.
// dayFirst=true pre-swaps the first two number groups before handing to
// chrono, for columns where DD/MM was detected (see columnLooksLikeDayFirst).
function normalizeDateTime(raw: string, dayFirst = false): { date: string; time: string; valid: boolean } {
  if (!raw?.trim()) return { date: '', time: '', valid: true };
  let input = raw.trim();
  if (dayFirst) {
    input = input.replace(/^(\d{1,2})([\/\-.])(\d{1,2})([\/\-.])(\d{2,4})/, '$3$2$1$4$5');
  }
  const results = chrono.parse(input);
  if (results.length === 0) return { date: raw, time: '', valid: false };
  const d = results[0].start.date();
  const hasTime = results[0].start.isCertain('hour');
  return {
    date: d.toISOString().split('T')[0],
    time: hasTime ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
    valid: true,
  };
}

// Scans a whole column at once — if ANY value has a first number > 12,
// the column must be DD/MM/YYYY, not MM/DD/YYYY.
function columnLooksLikeDayFirst(values: string[]): boolean {
  return values.some(v => {
    const m = v.match(/^(\d{1,2})[\/\-.]\d{1,2}[\/\-.]\d{2,4}/);
    return !!(m && parseInt(m[1], 10) > 12);
  });
}

// Extracts GSTIN / Tax ID from mapped column, column name fuzzy search, or 15-character GSTIN regex
function extractTaxIdFromRow(raw: Record<string, string>, mappedCol?: string | null): string {
  if (mappedCol && raw[mappedCol]?.trim()) {
    return raw[mappedCol].trim();
  }
  for (const [key, val] of Object.entries(raw || {})) {
    if (!val || typeof val !== 'string' || !val.trim()) continue;
    const cleanKey = key.replace(/\s+/g, ' ').trim().toLowerCase();
    if (/gstin|tax\s*id|vat\s*id|buyer\s*tax|tax\s*no|tax\s*num/i.test(cleanKey)) {
      return val.trim();
    }
  }
  for (const val of Object.values(raw || {})) {
    if (typeof val === 'string' && /\b([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1})\b/.test(val.trim())) {
      return val.trim();
    }
  }
  return '';
}

// Parses price/quantity strings in ANY locale format — "1,234.56",
// "1.234,56", "$1,200", "₹45.5", "1 234,50" — instead of rejecting
// anything parseFloat() can't handle directly. Never throws; worst case
// returns 0 so a row is never blocked by formatting alone.
function parseFlexibleNumber(raw: string): number {
  if (!raw?.trim()) return 0;
  const cleaned = raw.trim().replace(/[^\d.,\-]/g, '');
  if (!cleaned) return 0;
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const decimalSep = lastComma > lastDot ? ',' : '.';
  const thousandsSep = decimalSep === ',' ? '.' : ',';
  const normalized = cleaned.split(thousandsSep).join('').replace(decimalSep, '.');
  const n = parseFloat(normalized);
  return isNaN(n) ? 0 : n;
}

interface CustomFieldDef { id: string; label: string; column: string | null }

interface BatchRow {
  rowIndex: number;
  raw: Record<string, string>;
  errors: string[];
  warnings: string[];
}

type Stage = 'template' | 'upload' | 'mapping' | 'editing' | 'review' | 'position' | 'generating' | 'done';

interface BatchInvoiceGeneratorProps {
  customTemplates: ActiveTemplate[];
  activeCurrency: string;
  onAddLog: (action: string, details: string) => void;
  onClose?: () => void;
  outletId?: number | null;
}

const CHUNK_SIZE = 150; // rows per /generate-pdf request, then merged client-side

// Cached per unique string+lang — header labels and boilerplate text
// repeat across every row in a batch, so this avoids re-translating the
// same phrase hundreds of times.
const translateCache = new Map<string, Promise<string>>();

// Preload multiple texts in 1 SINGLE BATCH API CALL and populate translateCache
async function preloadBatchTranslations(texts: string[], lang: string): Promise<void> {
  if (!texts.length || lang === 'en') return;

  // Filter out items already in memory cache
  const uncached = Array.from(new Set(
    texts.map(t => t.trim()).filter(t => t && !translateCache.has(`${lang}:${t}`))
  ));

  if (!uncached.length) return;

  try {
    const res = await fetch(`${BACKEND}/translate/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
      },
      body: JSON.stringify({ texts: uncached, tl: lang }),
    });

    if (res.ok) {
      const data = await res.json();
      const list: string[] = Array.isArray(data?.translations) ? data.translations : uncached;
      uncached.forEach((orig, idx) => {
        let finalVal = list[idx] || orig;
        if (lang === 'ar' && /[a-zA-Z]/.test(finalVal)) {
          finalVal = toArabicScript(finalVal);
        }
        translateCache.set(`${lang}:${orig}`, Promise.resolve(finalVal));
      });
    }
  } catch (e) {
    console.warn('Batch preload failed, falling back to individual calls:', e);
  }
}

async function translateOnce(text: string, lang: string): Promise<{ ok: boolean; value: string }> {
  try {
    const res = await fetch(
      `${BACKEND}/translate?text=${encodeURIComponent(text.trim())}&tl=${lang}`,
      { headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {} }
    );
    if (!res.ok) return { ok: false, value: text };
    const data = await res.json();
    if (!data?.translated) return { ok: false, value: text };
    return { ok: true, value: data.translated };
  } catch {
    return { ok: false, value: text };
  }
}

// Converts any remaining untranslated Latin/English characters into Arabic script
// dynamically without requiring any hardcoded lists of cities or names.
function toArabicScript(str: string): string {
  if (/@/.test(str) || !/[a-zA-Z]/.test(str)) return str;

  const digraphs: [RegExp, string][] = [
    [/ph/gi, 'ف'], [/th/gi, 'ث'], [/sh/gi, 'ش'], [/kh/gi, 'خ'],
    [/ch/gi, 'تش'], [/gh/gi, 'غ'], [/dh/gi, 'ذ'], [/ee/gi, 'ي'],
    [/oo/gi, 'و'], [/ou/gi, 'و'], [/ai/gi, 'اي']
  ];
  const singles: Record<string, string> = {
    b: 'ب', p: 'ب', t: 'ت', c: 'ك', k: 'ك', q: 'ق',
    j: 'ج', g: 'غ', d: 'د', r: 'ر', z: 'ز', s: 'س',
    f: 'ف', v: 'ف', l: 'ل', m: 'م', n: 'ن', h: 'ه',
    w: 'و', y: 'ي', x: 'كس', a: 'ا', e: 'ي', i: 'ي',
    o: 'و', u: 'و'
  };

  let res = str;
  // Handle initial vowels at the start of words
  res = res.replace(/(^|\s)[iIeE]/g, '$1إ').replace(/(^|\s)[aA]/g, '$1أ');
  for (const [re, rep] of digraphs) res = res.replace(re, rep);
  res = res.replace(/[a-zA-Z]/g, ch => singles[ch.toLowerCase()] || ch);
  return res.replace(/,/g, '،');
}

async function translateCached(text: string, lang: string): Promise<string> {
  if (!text?.trim() || lang === 'en') return text;

  const raw = text.trim();

  // 1. Invoice header disambiguation: never translate commercial "Bill To" as draft law ("مشروع قانون")
  if (/^bill\s*to\s*:?$/i.test(raw)) {
    return lang === 'ar' ? 'فاتورة إلى:' : translateCached('Invoice To:', lang);
  }

  // 2. Comma-separated addresses: translate each part individually so the API
  // can properly identify cities and states (e.g. "Bengaluru" -> "بنغالورو", "Karnataka" -> "كارناتاكا")
  if (raw.includes(',') && !/@/.test(raw)) {
    const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
    const translatedParts = await Promise.all(parts.map(p => translateCached(p, lang)));
    return translatedParts.join(lang === 'ar' ? '، ' : ', ');
  }

  const key = `${lang}:${raw}`;
  if (translateCache.has(key)) return translateCache.get(key)!;

  const p = (async () => {
    let finalVal = raw;
    for (let attempt = 0; attempt < 3; attempt++) {
      const result = await translateOnce(raw, lang);
      if (result.ok && result.value) {
        finalVal = result.value;
        break;
      }
      if (attempt < 2) await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
    }

    // 3. Guarantee zero English in Arabic mode: if the API returned untranslated Latin letters,
    // convert them dynamically into Arabic script
    if (lang === 'ar' && /[a-zA-Z]/.test(finalVal)) {
      finalVal = toArabicScript(finalVal);
    }

    return finalVal;
  })();

  translateCache.set(key, p);
  return p;
}

async function runWithConcurrency<T, R>(
  items: T[], limit: number, worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  async function next(): Promise<void> {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => next()));
  return results;
}

// Helper to convert modern CSS oklch colors into standard RGB using the browser's 2D canvas
function sanitizeOklchColors(css: string): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return css.replace(/oklch\([^)]+\)/gi, '#1e293b');
    return css.replace(/oklch\([^)]+\)/gi, (match) => {
      ctx.fillStyle = '#000000';
      ctx.fillStyle = match;
      return ctx.fillStyle || '#000000';
    });
  } catch {
    return css.replace(/oklch\([^)]+\)/gi, '#1e293b');
  }
}

// 100% Client-side Exact A4 & Thermal PDF renderer with Professional Pagination & Margins
async function generatePdfClientSide(
  rowHtml: string,
  pageCss: string,
  pageWidthPx: number = 794,
  pageHeightPx: number = 1123,
  isThermal: boolean = false,
  invoiceNumber: string = ''
): Promise<ArrayBuffer> {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '0';
  container.style.top = '0';
  container.style.width = `${pageWidthPx}px`;
  container.style.zIndex = '-99999';
  container.style.opacity = '1';
  container.style.pointerEvents = 'none';
  container.style.background = '#ffffff';

  const targetId = `invoice-page-target-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const cleanCss = sanitizeOklchColors(pageCss);
  const cleanHtml = sanitizeOklchColors(rowHtml);

  container.innerHTML = `
    <style data-invoice-scoped-css>
      ${cleanCss}
      #${targetId} * {
        box-sizing: border-box;
      }
      #${targetId} {
        position: relative !important;
        margin: 0 !important;
        padding: 0 !important;
        width: ${pageWidthPx}px !important;
        min-height: ${isThermal ? 'auto' : `${pageHeightPx}px`} !important;
        height: auto !important;
        overflow: visible !important;
        background: #ffffff !important;
        display: block !important;
      }
    </style>
    <div id="${targetId}">
      ${cleanHtml}
    </div>
  `;

  document.body.appendChild(container);

  try {
    await new Promise(resolve => setTimeout(resolve, 150));

    const targetElement = container.querySelector(`#${targetId}`) as HTMLElement;

    // Collect clean bottom boundaries of rows, QR codes, paragraphs, and text blocks
    const elementBoundaries: number[] = [];
    targetElement.querySelectorAll('tr, p, img, canvas, table, .cw-element, h1, h2, h3, h4').forEach((el) => {
      const rect = (el as HTMLElement).getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const bottomPx = Math.round((rect.bottom - targetRect.top) * 2); // In 2x canvas pixels
      if (bottomPx > 50) {
        elementBoundaries.push(bottomPx);
      }
    });

    // Also include leaf text containers (e.g. captions under QR codes and notes)
    targetElement.querySelectorAll('div').forEach((el) => {
      if (el.children.length === 0 && (el.innerText || '').trim().length > 0) {
        const rect = el.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const bottomPx = Math.round((rect.bottom - targetRect.top) * 2);
        if (bottomPx > 50) elementBoundaries.push(bottomPx);
      }
    });

    elementBoundaries.sort((a, b) => a - b);
    const cleanBoundaries = Array.from(new Set(elementBoundaries));

    const fullHeight = Math.max(
      targetElement.scrollHeight,
      targetElement.offsetHeight,
      isThermal ? 300 : pageHeightPx
    );

    const canvas = await html2canvas(targetElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: pageWidthPx,
      height: fullHeight,
      windowWidth: pageWidthPx,
      windowHeight: fullHeight,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        const outerStyles = clonedDoc.querySelectorAll(
          'link[rel="stylesheet"], style:not([data-invoice-scoped-css])'
        );
        outerStyles.forEach(s => s.remove());
      },
    });

    let pdf: jsPDF;

    if (isThermal) {
      // Continuous roll for receipts
      const widthMm = pageWidthPx * 0.264583;
      const heightMm = fullHeight * 0.264583;
      pdf = new jsPDF({
        orientation: heightMm > widthMm ? 'p' : 'l',
        unit: 'mm',
        format: [widthMm, heightMm],
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', 0, 0, widthMm, heightMm, undefined, 'FAST');
    } else {
      // Exact ISO A4 setup
      pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const totalCanvasHeight = canvas.height;
      const pageCanvasHeight = Math.round(pageHeightPx * 2); // 2246px @ 2x

      // Single-page invoice: render directly if it fits within comfortable 60px tolerance
      if (totalCanvasHeight <= pageCanvasHeight + 60) {
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        return pdf.output('arraybuffer') as ArrayBuffer;
      }

      // --- Multi-Page Calculation with Professional Margins ---
      // Page 1 has 18mm bottom margin
      const page1MaxCanvasH = Math.round(((297 - 18) / 297) * pageCanvasHeight); // ~2110px
      // Continuation pages have 20mm top margin and 16mm bottom margin
      const contMaxCanvasH = Math.round(((297 - 20 - 16) / 297) * pageCanvasHeight); // ~1975px

      // 1. Pre-calculate page slices using smart row snapping
      const slices: { startY: number; height: number }[] = [];
      let tempY = 0;

      while (tempY < totalCanvasHeight) {
        const maxH = slices.length === 0 ? page1MaxCanvasH : contMaxCanvasH;

        // If remaining content fits within ~25mm of margin, finish on the current page
        if (tempY + maxH >= totalCanvasHeight - 220) {
          slices.push({ startY: tempY, height: totalCanvasHeight - tempY });
          break;
        }

        // Snap cut cleanly to the nearest row/element bottom boundary (looking back up to 350px)
        let chosenH = maxH;
        const targetCut = tempY + maxH;
        for (let i = cleanBoundaries.length - 1; i >= 0; i--) {
          const b = cleanBoundaries[i];
          if (b <= targetCut && b >= targetCut - 350) {
            chosenH = b - tempY;
            break;
          }
        }

        // Safety forward progress guarantee
        if (chosenH <= 150) {
          chosenH = maxH;
        }

        slices.push({ startY: tempY, height: chosenH });
        tempY += chosenH;
      }

      const totalPages = slices.length;

      // 2. Render each page slice with continuation headers and footers
      slices.forEach((slice, pageIdx) => {
        if (pageIdx > 0) {
          pdf.addPage('a4', 'portrait');
        }

        // Create canvas slice for this page's content
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = slice.height;
        const pCtx = pageCanvas.getContext('2d')!;
        pCtx.fillStyle = '#ffffff';
        pCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        pCtx.drawImage(
          canvas,
          0, slice.startY, canvas.width, slice.height,
          0, 0, canvas.width, slice.height
        );

        const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const sliceHeightMm = (slice.height / pageCanvasHeight) * 297;

        if (pageIdx === 0) {
          pdf.addImage(imgData, 'JPEG', 0, 0, 210, sliceHeightMm, undefined, 'FAST');
        } else {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8.5);
          pdf.setTextColor(51, 65, 85);
          const headerTitle = invoiceNumber ? `Invoice #: ${invoiceNumber} (Continued)` : 'Invoice (Continued)';
          pdf.text(headerTitle, 15, 11);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(100, 116, 139);
          pdf.text(`Page ${pageIdx + 1} of ${totalPages}`, 195, 11, { align: 'right' });

          pdf.setDrawColor(226, 232, 240);
          pdf.setLineWidth(0.25);
          pdf.line(15, 14, 195, 14);

          pdf.addImage(imgData, 'JPEG', 0, 18, 210, sliceHeightMm, undefined, 'FAST');
        }

        // Clean page indicator on all pages when multi-page
        if (totalPages > 1) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(148, 163, 184);
          pdf.text(`Page ${pageIdx + 1} of ${totalPages}`, 105, 292, { align: 'center' });
        }
      });
    }

    return pdf.output('arraybuffer') as ArrayBuffer;
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}

export default function BatchInvoiceGenerator({
  
  customTemplates, activeCurrency, onAddLog, onClose, outletId,
}: BatchInvoiceGeneratorProps) {
  const isMobileBatchEmbed =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('embed') ===
      'batch-generator';

  const allTemplates: ActiveTemplate[] = [
    ...TEMPLATE_PRESETS.map(t => ({ ...t, id: t.id || t.name })),
    ...customTemplates,
  ];

  const [stage, setStage] = useState<Stage>('template');
  const [batchTranslate, setBatchTranslate] = useState(false);
  const [batchLang, setBatchLang] = useState('ar');
  const [showAmountInWords, setShowAmountInWords] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    () => localStorage.getItem('batch_default_template_id') || allTemplates[0]?.id || ''
  );
  const [defaultTemplateId, setDefaultTemplateId] = useState(
    () => localStorage.getItem('batch_default_template_id') || ''
  );
  const selectedTemplate = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [taxMode, setTaxMode] = useState<TaxCalculationMode>('auto_calculate');

  // Seller's country, state, custom tax scheme and active tax ID
  const [sellerCountry, setSellerCountry] = useState('IN');
  const [sellerRegion, setSellerRegion] = useState('');
  const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(null);
  const [customTaxScheme, setCustomTaxScheme] = useState<CustomTaxSchemeConfig | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(activeCurrency || 'INR');
  const [currencyDisplayMode, setCurrencyDisplayMode] = useState<'symbol' | 'code'>(
    () => (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
  );

  const getSchemeById = (id: string | undefined): TaxScheme | undefined => {
    if ((id === 'custom' || id === 'outlet_custom') && customTaxScheme) {
      return { ...buildCustomScheme(customTaxScheme), id: id || 'custom' };
    }
    return getSchemeByIdBase(id);
  };

  useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || getStoredToken() || '';
    if (!jwt) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then((outlets: any[]) => {
        if (!Array.isArray(outlets) || outlets.length === 0) return;
        const outlet = (outletId ? outlets.find(o => o.id === outletId) : null) || outlets[0];
        if (outlet?.country) {
          setSellerCountry(outlet.country);
          const detectedCurr = outlet.currency || getCountryCurrency(outlet.country);
          if (detectedCurr) setSelectedCurrency(detectedCurr);
        } else if (outlet?.currency) {
          setSelectedCurrency(outlet.currency);
        }
        if (outlet?.region) setSellerRegion(outlet.region);
        if (outlet?.custom_tax_scheme) setCustomTaxScheme(outlet.custom_tax_scheme);
        const savedScheme = outlet?.tax_scheme_id || (outlet?.custom_tax_scheme ? 'outlet_custom' : null);
        if (savedScheme) setSelectedSchemeId(savedScheme);
      })
      .catch(() => {});
  }, [outletId]);

  const availableSchemes = useMemo(() => [
    ...(customTaxScheme ? [{ ...buildCustomScheme(customTaxScheme), id: 'outlet_custom', label: `⭐ ${customTaxScheme.name} (Custom)` }] : []),
    ...getSchemesForCountry(sellerCountry).filter(s => s.id !== 'custom'),
  ], [sellerCountry, customTaxScheme]);

  const taxScheme = useMemo(() => {
    if (selectedSchemeId) {
      const found = getSchemeById(selectedSchemeId);
      if (found) return found;
    }
    return getDefaultSchemeForCountryAndRegion(sellerCountry, sellerRegion);
  }, [sellerCountry, sellerRegion, selectedSchemeId, customTaxScheme]);

  // Helper to safely resolve currency whether given a 2-letter code ("ID") or full name ("Indonesia")
  const resolveCurrencyForCountry = (countryOrCode: string): string => {
    if (!countryOrCode) return 'USD';
    const direct = getCountryCurrency(countryOrCode);
    if (direct && direct !== 'USD') return direct;
    const found = getOrderedCountryList().find(
      c => c.name.toLowerCase() === countryOrCode.toLowerCase() || c.code.toLowerCase() === countryOrCode.toLowerCase()
    );
    return found ? getCountryCurrency(found.code) : (direct || 'USD');
  };

  // Automatically update currency whenever the selected country changes
  useEffect(() => {
    if (sellerCountry) {
      const autoCurr = resolveCurrencyForCountry(sellerCountry);
      if (autoCurr) {
        setSelectedCurrency(autoCurr);
      }
    }
  }, [sellerCountry]);

  const handleCountryChange = (c: string) => {
    setSellerCountry(c);
    setSellerRegion('');
    setSelectedSchemeId(null);
    if (c) {
      const autoCurr = resolveCurrencyForCountry(c);
      if (autoCurr) {
        setSelectedCurrency(autoCurr);
      }
    }
  };
  const [fileName, setFileName] = useState('');

  const dynamicFields: DynamicField[] = useMemo(
    () => (selectedTemplate ? extractDynamicFields(selectedTemplate) : []),
    [selectedTemplateId]
  );

  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [editedRows, setEditedRows] = useState<Record<number, Record<string, string>>>({});
  const [dayFirstFields, setDayFirstFields] = useState<Record<string, boolean>>({});
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);

  const allFields: FieldToMap[] = useMemo(() => [
    ...BASE_FIELDS,
    ...dynamicFields.map(f => ({ key: `dyn_${f.key}`, label: f.label })),
    ...customFields.filter(f => f.label.trim()).map(f => ({ key: `custom_${f.id}`, label: f.label })),
  ], [dynamicFields, customFields]);


  const [progress, setProgress] = useState({ stage: '', current: 0, total: 0 });
  const [rowErrors, setRowErrors] = useState<{ rowIndex: number; error: string }[]>([]);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const cancelRef = useRef(false);

  // Monthly invoice usage (shared across the whole plan/org) — fetched fresh
  // whenever the review step loads, so the warning reflects the latest count.
  const [usage, setUsage] = useState<{ count: number; limit: number | null; reset_date: string; plan?: string } | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res = await fetch(`${BACKEND}/invoices/monthly-usage?source=batch`, {
        headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {},
      });
      const data = await res.json();
      setUsage(data);
    } catch {
      // keep last-known usage on error — don't block the flow over this
    } finally {
      setUsageLoading(false);
    }
  }, []);

  // ---------- Step 1→2: CSV upload ----------
  const applyParsedData = (headers: string[], rows: Record<string, string>[]) => {
    setCsvHeaders(headers);
    setCsvRows(rows);
    const guessed = autoMapColumns(headers, allFields);

    // Smart auto-match for grouping column and extracted letterhead fields
    if (!guessed['group_id']) {
      const matched = headers.find(h => /^(invoice\s?#?|inv\s?#?|order\s?id|po\s?#?|bill\s?no)/i.test(h.trim()));
      if (matched) guessed['group_id'] = matched;
    }
    if (!guessed['client_name']) {
      const matched = headers.find(h => /^(client|customer|recipient)\s*(name)?$/i.test(h.trim()));
      if (matched) guessed['client_name'] = matched;
    }
    if (!guessed['client_tax_id']) {
      const matched = headers.find(h => /gstin|tax\s*id|vat\s*id|buyer\s*tax/i.test(h.trim()));
      if (matched) guessed['client_tax_id'] = matched;
    }
    if (!guessed['client_region']) {
      const matched = headers.find(h => /(buyer\s*state|buyer\s*province|state|province|region)/i.test(h.trim()));
      if (matched) guessed['client_region'] = matched;
    }
    if (!guessed['client_address']) {
      const matched = headers.find(h => /^address$/i.test(h.trim()));
      if (matched) guessed['client_address'] = matched;
    }
    if (!guessed['b2b_tax_rate']) {
      const matched = headers.find(h => /b2b\s*(tax|rate)/i.test(h.trim()));
      if (matched) guessed['b2b_tax_rate'] = matched;
    }
    if (!guessed['tax_inclusive']) {
      const matched = headers.find(h => /inclusive/i.test(h.trim()));
      if (matched) guessed['tax_inclusive'] = matched;
    }
    if (!guessed['tax_name']) {
      const matched = headers.find(h => /^(tax\s?name|tax\s?type|tax\s?label)/i.test(h.trim()));
      if (matched) guessed['tax_name'] = matched;
    }
    if (!guessed['tax_amount']) {
      const matched = headers.find(h => /^(total\s?tax|tax\s?total|vat\s?total)/i.test(h.trim()))
        || headers.find(h => /^(tax\s?amount|tax\s?val|vat\s?amount|gst\s?amount|tax)$/i.test(h.trim()));
      if (matched) guessed['tax_amount'] = matched;
    }
    if (!guessed['subtotal']) {
      const matched = headers.find(h => /^(subtotal|sub\s?total|base\s?amount|net\s?amount)/i.test(h.trim()));
      if (matched) guessed['subtotal'] = matched;
    }
    if (!guessed['total_amount']) {
      const matched = headers.find(h => /^(grand\s?total|total\s?amount|final\s?amount|gross\s?amount|total)$/i.test(h.trim()));
      if (matched) guessed['total_amount'] = matched;
    }

    // Auto-switch to exact file mode if file has pre-calculated totals
    if (guessed['total_amount'] || guessed['tax_amount'] || guessed['subtotal']) {
      setTaxMode('exact_file');
    }

    setMapping(guessed);

    // Detect DD/MM vs MM/DD per date column by scanning the whole column
    const dayFirst: Record<string, boolean> = {};
    ['invoice_date', 'due_date'].forEach(key => {
      const col = guessed[key];
      if (col) dayFirst[key] = columnLooksLikeDayFirst(rows.map(r => r[col] || ''));
    });
    setDayFirstFields(dayFirst);

    setStage('mapping');
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          if (wb.SheetNames.length === 0) return alert('Excel file has no sheets.');

          const allProcessedRows: Record<string, string>[] = [];
          const allHeadersSet = new Set<string>();

          wb.SheetNames.forEach((sheetName) => {
            const sheet = wb.Sheets[sheetName];
            const aoa: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
            if (!aoa || aoa.length === 0) return;

            // 1. Locate the items table header (Description, Qty, Rate...)
            const headerRowIdx = aoa.findIndex(row => 
              row.some((cell: any) => /desc|item|qty|rate|price|product/i.test(String(cell ?? '')))
            );
            if (headerRowIdx === -1) return;

            // 2. Extract Invoice #, Client Name, Address, GSTIN, and exact bottom summary
            let metaInvoiceNo = '';
            let metaDate = '';
            let metaDueDate = '';
            let metaClientName = '';
            let metaAddress = '';
            let metaGstin = '';
            let metaEmail = '';
            let metaSubtotal = '';
            let metaTaxName = '';
            let metaTotalTax = '';
            let metaGrandTotal = '';

            // Accurately scan bottom summary rows below the table
            aoa.forEach((row, rIdx) => {
              if (rIdx <= headerRowIdx || !Array.isArray(row)) return;
              row.forEach((cell, cIdx) => {
                const s = String(cell ?? '').trim();
                if (!s) return;
                
                // 1. Subtotal
                if (/^sub\s*total/i.test(s)) {
                  const val = String(row.slice(cIdx + 1).find(c => String(c ?? '').trim() !== '') ?? '').trim();
                  if (val && !isNaN(parseFlexibleNumber(val))) metaSubtotal = val;
                }
                // 2. Exact Tax Name and Value (e.g. "Total Tax:", "Total Tax (18%):", "VAT (15%):", "GST:", "Tax:")
                else if (/^(total\s*tax|tax\s*total|vat|gst|sales\s*tax|tax)(\s*\([^\)]*\))?\s*:?$/i.test(s) && !/tax\s*%/i.test(s)) {
                  const labelClean = s.replace(/:$/, '').trim();
                  if (labelClean) metaTaxName = labelClean;
                  const val = String(row.slice(cIdx + 1).find(c => String(c ?? '').trim() !== '') ?? '').trim();
                  if (val && !isNaN(parseFlexibleNumber(val))) metaTotalTax = val;
                }
                // 3. Grand Total (ignoring "Line Total")
                else if (/^(grand\s*total|gross\s*total|total\s*amount|total\s*due)/i.test(s) || (/^total\s*:?$/i.test(s) && !/line/i.test(s))) {
                  const val = String(row.slice(cIdx + 1).find(c => String(c ?? '').trim() !== '') ?? '').trim();
                  if (val && !isNaN(parseFlexibleNumber(val))) metaGrandTotal = val;
                }
              });
            });

            const topRows = aoa.slice(0, headerRowIdx);
            topRows.forEach((row, rIdx) => {
              const rowStr = row.map(c => String(c ?? '').trim()).join(' ');
              
              row.forEach((cell, cIdx) => {
                const s = String(cell ?? '').trim();
                if (/invoice\s*#?:?/i.test(s)) {
                  const val = String(row[cIdx + 1] ?? '').trim();
                  if (val) metaInvoiceNo = val;
                }
                if (/^date:?/i.test(s)) {
                  const val = String(row[cIdx + 1] ?? '').trim();
                  if (val) metaDate = val;
                }
                if (/due\s*date:?/i.test(s)) {
                  const val = String(row[cIdx + 1] ?? '').trim();
                  if (val) metaDueDate = val;
                }
              });

              if (/bill\s*to/i.test(rowStr)) {
                const nameRow = topRows[rIdx + 1];
                if (nameRow) metaClientName = String(nameRow.find(c => String(c ?? '').trim() !== '') ?? '').trim();
                const addrRow = topRows[rIdx + 2];
                if (addrRow) metaAddress = String(addrRow.find(c => String(c ?? '').trim() !== '') ?? '').trim();
                const taxRow = topRows[rIdx + 3];
                if (taxRow) {
                  const taxStr = taxRow.map(c => String(c ?? '').trim()).join(' ');
                  const gstinMatch = taxStr.match(/\b([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1})\b/);
                  if (gstinMatch) metaGstin = gstinMatch[1];
                  const emailMatch = taxStr.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
                  if (emailMatch) metaEmail = emailMatch[1];
                }
              }
            });

            const tableHeaders = (aoa[headerRowIdx] as any[])
              .map(h => String(h ?? '').trim())
              .filter(Boolean);

            tableHeaders.forEach(h => allHeadersSet.add(h));
            if (metaInvoiceNo) allHeadersSet.add('Invoice #');
            if (metaClientName) allHeadersSet.add('Client Name');
            if (metaAddress) allHeadersSet.add('Address');
            if (metaGstin) allHeadersSet.add('Buyer GSTIN');
            if (metaEmail) allHeadersSet.add('Email');
            if (metaDate) allHeadersSet.add('Date');
            if (metaDueDate) allHeadersSet.add('Due Date');
            if (metaSubtotal) allHeadersSet.add('Subtotal');
            if (metaTotalTax) allHeadersSet.add(metaTaxName || 'Total Tax');
            if (metaGrandTotal) allHeadersSet.add('Grand Total');
            if (metaTaxName) allHeadersSet.add('Tax Name');

            // 3. Extract items
            const rawItemRows = aoa.slice(headerRowIdx + 1);
            rawItemRows.forEach((r, rIdx) => {
              const nonBlank = r.map(c => String(c ?? '').trim()).filter(Boolean);
              if (nonBlank.length === 0) return;

              const firstCell = nonBlank[0].toLowerCase();
              if (/^(subtotal|total|tax|grand total|notes|terms|thank you|payment due|conditions|bank details)/i.test(firstCell)) return;

              const rowObj: Record<string, string> = {};

              // If sheet is a single invoice with letterhead, use letterhead fields
              if (metaInvoiceNo) rowObj['Invoice #'] = metaInvoiceNo;
              if (metaClientName) rowObj['Client Name'] = metaClientName;
              if (metaAddress) rowObj['Address'] = metaAddress;
              if (metaGstin) rowObj['Buyer GSTIN'] = metaGstin;
              if (metaEmail) rowObj['Email'] = metaEmail;
              if (metaDate) rowObj['Date'] = metaDate;
              if (metaDueDate) rowObj['Due Date'] = metaDueDate;
              if (metaSubtotal) rowObj['Subtotal'] = metaSubtotal;
              if (metaTotalTax) {
                rowObj[metaTaxName || 'Total Tax'] = metaTotalTax;
                rowObj['Total Tax'] = metaTotalTax;
              }
              if (metaGrandTotal) rowObj['Grand Total'] = metaGrandTotal;
              if (metaTaxName) rowObj['Tax Name'] = metaTaxName;

              // Copy all row columns (overrides letterhead if column exists per row)
              (aoa[headerRowIdx] as any[]).forEach((h, colIdx) => {
                const headerName = String(h ?? '').trim();
                const cellVal = String(r[colIdx] ?? '').trim();
                if (headerName && cellVal !== '') {
                  rowObj[headerName] = cellVal;
                }
              });

              allProcessedRows.push(rowObj);
            });
          });

          if (allProcessedRows.length === 0) {
            return alert('Could not find any items in the Excel file.');
          }

          const finalHeaders = Array.from(allHeadersSet);
          applyParsedData(finalHeaders, allProcessedRows);
        } catch (err: any) {
          alert(`Excel parse failed: ${err?.message || err}`);
        }
      };
      reader.onerror = () => alert('Failed to read Excel file.');
      reader.readAsArrayBuffer(file);
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const rows = results.data as Record<string, string>[];
        applyParsedData(headers, rows);
      },
      error: (err) => alert(`CSV parse failed: ${err.message}`),
    });
  };

  // ---------- Step 2→3: validate rows against current mapping ----------
  const validatedRows: BatchRow[] = useMemo(() => {
    return csvRows.map((raw, i) => {
      const errors: string[] = [];
      const warnings: string[] = [];
      // client_name is optional — no longer blocks row validation.

      // Qty/rate no longer block generation — any format gets parsed
      // flexibly (see parseFlexibleNumber) and falls back to 0/1 rather
      // than failing the row. We just surface a heads-up warning.
      const qtyCol = mapping['qty'];
      if (qtyCol && raw[qtyCol] && parseFlexibleNumber(raw[qtyCol]) === 0 && raw[qtyCol].trim() !== '0') {
        warnings.push(`Quantity "${raw[qtyCol]}" couldn't be read — will default to 1`);
      }
      const rateCol = mapping['rate'];
      if (rateCol && raw[rateCol] && parseFlexibleNumber(raw[rateCol]) === 0 && raw[rateCol].trim() !== '0') {
        warnings.push(`Rate/Price "${raw[rateCol]}" couldn't be read — will default to 0`);
      }

      // Dynamic fields that look like tracking/order/serial numbers get a
      // warning (not an error) when blank — they'll be auto-generated.
      dynamicFields.forEach(f => {
        const col = mapping[`dyn_${f.key}`];
        const val = col ? raw[col]?.trim() : '';
        if (!val && AUTO_FALLBACK_FIELD_PATTERN.test(f.key)) {
          warnings.push(`${f.label} blank — will auto-generate`);
        }
      });

      return { rowIndex: i, raw, errors, warnings };
    });
  }, [csvRows, mapping, dynamicFields]);

  const errorRowCount = validatedRows.filter(r => r.errors.length > 0).length;
  const validRows = validatedRows.filter(r => r.errors.length === 0);

  // Group rows by group_id (Order ID / Invoice #). 
  // If not mapped, each row forms its own single-item invoice.
  const invoiceGroups = useMemo(() => {
    const groupCol = mapping['group_id'];
    const groupsMap = new Map<string, BatchRow[]>();

    validRows.forEach((row, idx) => {
      const edited = editedRows[row.rowIndex] || {};
      const rawData = { ...row.raw, ...edited };
      const rawKey = groupCol ? (rawData[groupCol]?.trim() || '') : '';
      const key = rawKey || `__single_${row.rowIndex}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, []);
      }
      groupsMap.get(key)!.push(row);
    });

    return Array.from(groupsMap.entries()).map(([key, rows], groupIndex) => {
      const primaryRow = rows[0];
      const editedPrimary = editedRows[primaryRow.rowIndex] || {};
      const primaryData = { ...primaryRow.raw, ...editedPrimary };

      // 1. Inspect Buyer Tax ID (GSTIN / VAT ID) for B2B detection
      const rawTaxId = extractTaxIdFromRow(primaryData, mapping['client_tax_id']);
      const taxIdMeta = inspectTaxId(rawTaxId, sellerCountry);
      const isB2B = taxIdMeta.isValid || rawTaxId.length >= 8;

      // 2. Resolve Buyer Region: Full 01-38 Official Indian GST State & UT Code Dictionary
      const regionCol = mapping['client_region'];
      const gstinPrefix = rawTaxId.length >= 2 ? rawTaxId.slice(0, 2) : '';
      const GST_STATE_CODE_MAP: Record<string, string> = {
        '01': 'Jammu and Kashmir',
        '02': 'Himachal Pradesh',
        '03': 'Punjab',
        '04': 'Chandigarh',
        '05': 'Uttarakhand',
        '06': 'Haryana',
        '07': 'Delhi',
        '08': 'Rajasthan',
        '09': 'Uttar Pradesh',
        '10': 'Bihar',
        '11': 'Sikkim',
        '12': 'Arunachal Pradesh',
        '13': 'Nagaland',
        '14': 'Manipur',
        '15': 'Mizoram',
        '16': 'Tripura',
        '17': 'Meghalaya',
        '18': 'Assam',
        '19': 'West Bengal',
        '20': 'Jharkhand',
        '21': 'Odisha',
        '22': 'Chhattisgarh',
        '23': 'Madhya Pradesh',
        '24': 'Gujarat',
        '25': 'Daman and Diu',
        '26': 'Dadra and Nagar Haveli and Daman and Diu',
        '27': 'Maharashtra',
        '28': 'Andhra Pradesh',
        '29': 'Karnataka',
        '30': 'Goa',
        '31': 'Lakshadweep',
        '32': 'Kerala',
        '33': 'Tamil Nadu',
        '34': 'Puducherry',
        '35': 'Andaman and Nicobar Islands',
        '36': 'Telangana',
        '37': 'Andhra Pradesh',
        '38': 'Ladakh',
        '97': 'Other Territory'
      };

      // Also fall back to city/state keywords in client address if GSTIN is not present
      const addressCol = mapping['client_address'];
      const rawAddress = [
        regionCol ? primaryData[regionCol] : '',
        addressCol ? primaryData[addressCol] : '',
        primaryData['Address'] || '',
      ].filter(Boolean).join(' ');

      let detectedStateFromAddress = '';
      if (/kerala|kochi|cochin|trivandrum|thiruvananthapuram|calicut|kozhikode/i.test(rawAddress)) detectedStateFromAddress = 'Kerala';
      else if (/gujarat|morbi|ahmedabad|surat|vadodara|rajkot/i.test(rawAddress)) detectedStateFromAddress = 'Gujarat';
      else if (/karnataka|bengaluru|bangalore|mysore|hubli|mangalore/i.test(rawAddress)) detectedStateFromAddress = 'Karnataka';
      else if (/maharashtra|mumbai|pune|nagpur|nashik|thane/i.test(rawAddress)) detectedStateFromAddress = 'Maharashtra';
      else if (/delhi|new delhi|connaught place|dwarka|saket/i.test(rawAddress)) detectedStateFromAddress = 'Delhi';
      else if (/tamil\s*nadu|chennai|madras|coimbatore|madurai/i.test(rawAddress)) detectedStateFromAddress = 'Tamil Nadu';
      else if (/telangana|hyderabad|secunderabad/i.test(rawAddress)) detectedStateFromAddress = 'Telangana';
      else if (/andhra\s*pradesh|visakhapatnam|vijayawada|tirupati/i.test(rawAddress)) detectedStateFromAddress = 'Andhra Pradesh';
      else if (/west\s*bengal|kolkata|calcutta|howrah/i.test(rawAddress)) detectedStateFromAddress = 'West Bengal';
      else if (/uttar\s*pradesh|noida|greater noida|lucknow|kanpur|varanasi|agra/i.test(rawAddress)) detectedStateFromAddress = 'Uttar Pradesh';
      else if (/haryana|gurgaon|gurugram|faridabad|panipat/i.test(rawAddress)) detectedStateFromAddress = 'Haryana';
      else if (/rajasthan|jaipur|jodhpur|udaipur/i.test(rawAddress)) detectedStateFromAddress = 'Rajasthan';
      else if (/puducherry|pondicherry/i.test(rawAddress)) detectedStateFromAddress = 'Puducherry';
      else if (/punjab|ludhiana|amritsar|jalandhar/i.test(rawAddress)) detectedStateFromAddress = 'Punjab';
      else if (/bihar|patna/i.test(rawAddress)) detectedStateFromAddress = 'Bihar';
      else if (/odisha|orissa|bhubaneswar|cuttack/i.test(rawAddress)) detectedStateFromAddress = 'Odisha';
      else if (/madhya\s*pradesh|indore|bhopal/i.test(rawAddress)) detectedStateFromAddress = 'Madhya Pradesh';
      else if (/goa|panaji|margao/i.test(rawAddress)) detectedStateFromAddress = 'Goa';

      const buyerRegion = (regionCol ? primaryData[regionCol]?.trim() : '')
        || (gstinPrefix && GST_STATE_CODE_MAP[gstinPrefix] ? GST_STATE_CODE_MAP[gstinPrefix] : '')
        || detectedStateFromAddress
        || taxIdMeta.detectedRegion
        || '';

      // 3. Process Line Items (Applying B2B tax rates if B2B)
      const lineItems = rows.map((r, itemIdx) => {
        const editedItem = editedRows[r.rowIndex] || {};
        const itemData = { ...r.raw, ...editedItem };
        const descCol = mapping['description'];
        const qtyCol = mapping['qty'];
        const rateCol = mapping['rate'];
        const taxCol = mapping['tax_rate'];
        const b2bTaxCol = mapping['b2b_tax_rate'];

        const description = (descCol ? itemData[descCol] : '') || `Item ${itemIdx + 1}`;
        const qty = parseFlexibleNumber(qtyCol ? itemData[qtyCol] : '') || 1;
        const rate = parseFlexibleNumber(rateCol ? itemData[rateCol] : '') || 0;
        
        // Scheme default rate (e.g. 18% for India GST)
        const schemeDefaultRate = (taxScheme as any).defaultRate
          || taxScheme.components?.reduce((sum, c) => sum + (c.rate || 0), 0)
          || (taxScheme as any).rate
          || 18;

        const hasExplicitTaxCol = !!(taxCol && itemData[taxCol] !== undefined && itemData[taxCol].trim() !== '');
        const fileTaxRate = hasExplicitTaxCol ? parseFlexibleNumber(itemData[taxCol]) : 0;
        const standardTaxRate = (taxMode === 'auto_calculate' && !hasExplicitTaxCol) ? schemeDefaultRate : fileTaxRate;
        const b2bTaxRate = parseFlexibleNumber(b2bTaxCol ? itemData[b2bTaxCol] : '');
        
        // If B2B is active and special B2B tax rate exists, use it (allows 0% B2B tax)
        const effectiveTaxRate = (isB2B && b2bTaxCol && itemData[b2bTaxCol] !== undefined && itemData[b2bTaxCol].trim() !== '' && !isNaN(b2bTaxRate))
          ? b2bTaxRate
          : standardTaxRate;

        // Support Tax Inclusive column (e.g. Rahul Sharma)
        const taxInclusiveCol = mapping['tax_inclusive'];
        const rawInclusive = taxInclusiveCol ? (itemData[taxInclusiveCol]?.trim().toLowerCase() || '') : '';
        const isInclusive = rawInclusive === 'yes' || rawInclusive === 'true' || rawInclusive === '1';

        let lineBase = qty * rate;
        let lineTaxAmount = 0;

        if (isInclusive && effectiveTaxRate > 0) {
          const grossAmount = qty * rate;
          lineBase = grossAmount / (1 + effectiveTaxRate / 100);
          lineTaxAmount = grossAmount - lineBase;
        } else {
          lineBase = qty * rate;
          lineTaxAmount = lineBase * (effectiveTaxRate / 100);
        }

        return {
          id: `item-${r.rowIndex}-${itemIdx}`,
          description,
          qty,
          rate: isInclusive ? lineBase / qty : rate,
          taxRate: effectiveTaxRate,
          taxAmount: lineTaxAmount,
          total: lineBase + lineTaxAmount,
        };
      });

      // Check for file-level explicit totals
      const fileSubtotalCol = mapping['subtotal'];
      const fileTaxAmountCol = mapping['tax_amount'];
      const fileTotalCol = mapping['total_amount'];
      const fileTaxNameCol = mapping['tax_name'];

      // Extract exact tax name from Excel metadata or mapped header
      const rawFileTaxName = (fileTaxNameCol ? primaryData[fileTaxNameCol]?.trim() : '')
        || primaryData['Tax Name']
        || (fileTaxAmountCol && !/amount|val/i.test(fileTaxAmountCol) ? fileTaxAmountCol : '')
        || 'Total Tax';

      const cleanFileTaxName = rawFileTaxName
        .replace(/\s*\([^)]*\)/g, '')
        .replace(/\s*\d+(\.\d+)?%/g, '')
        .replace(/:\s*$/, '')
        .trim() || 'Total Tax';

      // Prioritize active UI selected tax scheme name over custom outlet fallback
      const rawSchemeName = taxScheme.components?.[0]?.name || taxScheme.label || customTaxScheme?.name || 'Tax';
      const cleanCountryTaxName = rawSchemeName
        .replace(/\s*\([^)]*\)/g, '')
        .replace(/\s*\d+(\.\d+)?%/g, '')
        .replace(/:\s*$/, '')
        .replace(/^[⭐\s]+/, '')
        .trim() || 'Tax';

      // Sum line-level items
      const computedSubtotal = lineItems.reduce((sum, item) => sum + (item.qty * item.rate), 0);
      const computedTotalTax = lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);

      // Read exact values directly from sheet summary metadata or mapped columns
      const summaryTax = parseFlexibleNumber(primaryData['Total Tax']) || (fileTaxAmountCol ? parseFlexibleNumber(primaryData[fileTaxAmountCol]) : 0);
      const itemsTaxSum = fileTaxAmountCol ? rows.reduce((sum, r) => sum + parseFlexibleNumber((editedRows[r.rowIndex] || r.raw)[fileTaxAmountCol]), 0) : 0;
      const exactTaxAmount = summaryTax > 0 ? summaryTax : (itemsTaxSum > 0 ? itemsTaxSum : computedTotalTax);

      const summarySubtotal = parseFlexibleNumber(primaryData['Subtotal']) || (fileSubtotalCol ? parseFlexibleNumber(primaryData[fileSubtotalCol]) : 0);
      const exactSubtotal = summarySubtotal > 0 ? summarySubtotal : computedSubtotal;

      const summaryGrandTotal = parseFlexibleNumber(primaryData['Grand Total']) || (fileTotalCol ? parseFlexibleNumber(primaryData[fileTotalCol]) : 0);
      const exactGrandTotal = summaryGrandTotal > 0 ? summaryGrandTotal : (exactSubtotal + exactTaxAmount);

      // 1. Calculate rate and check for uniform rate
      const distinctRates = Array.from(new Set(lineItems.map(i => i.taxRate).filter(r => r > 0)));
      const isUniformRate = distinctRates.length === 1;
      const uniformRate = isUniformRate ? distinctRates[0] : 0;

      const subtotal = exactSubtotal;
      let totalTax = exactTaxAmount;
      let totalAmount = exactGrandTotal;

      if (totalTax === 0) {
        const fallbackRate = isUniformRate ? uniformRate : ((taxScheme as any).defaultRate || 11);
        totalTax = +(subtotal * (fallbackRate / 100)).toFixed(2);
        totalAmount = +(subtotal + totalTax).toFixed(2);
      }

      // Exact percentage for display label
      const exactEffectiveRate = subtotal > 0 && totalTax > 0
        ? +((totalTax / subtotal) * 100).toFixed(2)
        : (isUniformRate ? uniformRate : ((taxScheme as any).defaultRate || 11));

      // 2. Resolve intra-state vs inter-state
      const isIntraState = !sellerRegion || (buyerRegion && sellerRegion.toLowerCase() === buyerRegion.toLowerCase());
      const effectiveSellerRegion = sellerRegion || buyerRegion;

      let taxLines: Array<{ label: string; rate: number; amount?: number }> = [];

      if (taxMode === 'exact_file') {
        // Mode 1: Exact from File
        if (totalTax > 0) {
          const displayLabel = cleanFileTaxName.includes('%')
            ? cleanFileTaxName
            : (exactEffectiveRate > 0 ? `${cleanFileTaxName} (${exactEffectiveRate}%)` : cleanFileTaxName);
          taxLines = [{
            label: displayLabel,
            rate: subtotal > 0 ? (totalTax / subtotal) * 100 : 0,
            amount: +totalTax.toFixed(2),
          }];
        }
      } else if (taxMode === 'file_amounts_country_name') {
        // Mode 2: Exact File amounts + Percentage Label
        // Only apply CGST/SGST/IGST if Country is explicitly India (IN)
        if (sellerCountry === 'IN' && taxScheme.kind === 'component' && totalTax > 0) {
          if (isIntraState) {
            const halfAmount = +(totalTax / 2).toFixed(2);
            const otherHalf = +(totalTax - halfAmount).toFixed(2);
            const halfRate = isUniformRate && uniformRate > 0 ? +(uniformRate / 2).toFixed(2) : +(exactEffectiveRate / 2).toFixed(2);
            taxLines = [
              { label: `CGST (${halfRate}%)`, rate: subtotal > 0 ? (halfAmount / subtotal) * 100 : 0, amount: halfAmount },
              { label: `SGST (${halfRate}%)`, rate: subtotal > 0 ? (otherHalf / subtotal) * 100 : 0, amount: otherHalf },
            ];
          } else {
            const igstRate = isUniformRate && uniformRate > 0 ? uniformRate : exactEffectiveRate;
            taxLines = [
              { label: `IGST (${igstRate}%)`, rate: subtotal > 0 ? (totalTax / subtotal) * 100 : 0, amount: +totalTax.toFixed(2) },
            ];
          }
        } else if (taxScheme.components && taxScheme.components.length > 1 && totalTax > 0) {
          // Multi-component international schemes (e.g. Canada GST + PST)
          const totalSchemeRate = taxScheme.components.reduce((sum, c) => sum + (c.rate || 0), 0) || 1;
          taxLines = taxScheme.components.map(comp => {
            const ratio = (comp.rate || 0) / totalSchemeRate;
            const compAmount = +(totalTax * ratio).toFixed(2);
            return {
              label: `${comp.name} (${comp.rate}%)`,
              rate: comp.rate,
              amount: compAmount,
            };
          });
        } else if (totalTax > 0) {
          // Single country tax (Indonesia PPN, VAT, Sales Tax, etc.)
          const rateVal = isUniformRate && uniformRate > 0 ? uniformRate : (taxScheme.components?.[0]?.rate || exactEffectiveRate);
          taxLines = [{
            label: `${cleanCountryTaxName} (${rateVal}%)`,
            rate: subtotal > 0 ? (totalTax / subtotal) * 100 : 0,
            amount: +totalTax.toFixed(2),
          }];
        }
      } else {
        // Mode 3: Auto-Calculate all from Country Scheme
        if (sellerCountry === 'IN' && taxScheme.kind === 'component') {
          const calcRate = isUniformRate ? uniformRate : exactEffectiveRate;
          const breakdown = computeTaxBreakdown(taxScheme, subtotal, { sellerRegion: effectiveSellerRegion, buyerRegion }, calcRate);
          taxLines = breakdown.lines.map(l => {
            const cleanCompName = l.name.replace(/\s*\([^)]*\)/g, '').replace(/\s*\d+(\.\d+)?%/g, '').trim();
            return {
              label: `${cleanCompName} (${l.rate}%)`,
              rate: subtotal > 0 ? (l.amount / subtotal) * 100 : l.rate,
              amount: +l.amount.toFixed(2),
            };
          });
          totalTax = +taxLines.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2);
          totalAmount = +(subtotal + totalTax).toFixed(2);
        } else {
          const calcRate = isUniformRate ? uniformRate : (taxScheme.components?.[0]?.rate || exactEffectiveRate);
          totalTax = +(subtotal * (calcRate / 100)).toFixed(2);
          totalAmount = +(subtotal + totalTax).toFixed(2);
          taxLines = [{
            label: `${cleanCountryTaxName} (${calcRate}%)`,
            rate: subtotal > 0 ? (totalTax / subtotal) * 100 : 0,
            amount: totalTax,
          }];
        }
      }

      const customInvoiceNum = key.startsWith('__single_') ? '' : key;

      return {
        groupIndex,
        groupKey: key,
        customInvoiceNum,
        primaryRow,
        primaryData,
        rows,
        lineItems,
        subtotal,
        totalTax,
        totalAmount,
        taxLines,
        isB2B,
        rawTaxId,
        buyerRegion,
        taxIdMeta,
      };
    });
  }, [validRows, mapping, editedRows, taxScheme, sellerCountry, sellerRegion, taxMode]);

  const templateHasOwnQr = useMemo(
    () => (selectedTemplate ? selectedTemplate.elements.some(el => el.type === 'qr') : false),
    [selectedTemplateId]
  );
  // Page-space (unscaled) coords for the auto-injected QR. null = use the
  // default bottom-center placement computed in renderRowToHtml.
  const [qrPosition, setQrPosition] = useState<{ x: number; y: number } | null>(null);
  // Alignment used when qrPosition is null (no manual drag) — x is computed
  // from this, y is always computed dynamically per-row from that row's
  // real content height, so the QR never overlaps footer text regardless
  // of how long any given row's content is.
  const [qrAlign, setQrAlign] = useState<'left' | 'center' | 'right'>('left');
  const [includeQr, setIncludeQr] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewDims, setPreviewDims] = useState({ width: 794, height: 1123 });

  const buildQrPreview = useCallback(async () => {
    if (invoiceGroups.length === 0 || !selectedTemplate) return;
    const pc = PAGE_CONFIGS[selectedTemplate.pageSize || 'A4'] || PAGE_CONFIGS.A4;
    const sampleGroup = invoiceGroups[0];
    const bodyHtml = await renderRowToHtml(
      sampleGroup.primaryRow, selectedTemplate, mapping, dynamicFields, genFallback, selectedCurrency,
      sampleGroup.customInvoiceNum || 'SAMPLE-0001', '', qrPosition, customFields, dayFirstFields, qrAlign,
      batchTranslate ? batchLang : '', sampleGroup.lineItems, sampleGroup.taxLines,
      showAmountInWords, sampleGroup.totalAmount, currencyDisplayMode
    );
    // renderRowToHtml now returns body-only markup (no CSS) since batch
    // generation adds the stylesheet once for the whole merged document.
    // This preview renders standalone in its own iframe, so it needs the
    // CSS added back here — otherwise every Tailwind class is unstyled,
    // which is why the page was rendering as a solid purple block.
    const css = await getPageCss();
    setPreviewHtml(`<style>${css}</style>${bodyHtml}`);
    setPreviewDims({
      width: pc.width,
      height: (selectedTemplate.pageSize || '').startsWith('THERMAL_') ? 500 : pc.height,
    });
  }, [invoiceGroups, selectedTemplate, mapping, dynamicFields, selectedCurrency, currencyDisplayMode, customFields, dayFirstFields, qrAlign, qrPosition, batchTranslate, batchLang, showAmountInWords]);

  useEffect(() => {
    if (stage === 'position') buildQrPreview();
  }, [stage, buildQrPreview]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const templateQrElement = useMemo(() => {
    return (selectedTemplate?.elements.find((el: any) => el.type === 'qr') as any) || null;
  }, [selectedTemplateId]);

  const qrSizeForTemplate = useMemo(() => {
    const fallback = (selectedTemplate?.pageSize || '').startsWith('THERMAL_') ? 80 : 100;
    return templateQrElement?.width || fallback;
  }, [templateQrElement, selectedTemplate?.pageSize]);

  const updateMapping = (fieldKey: string, header: string | null) => {
    setMapping(prev => ({ ...prev, [fieldKey]: header }));
  };

  const fallbackCounter = useRef(0);
  const genFallback = (fieldKey: string, rowIndex: number) => {
    fallbackCounter.current += 1;
    const prefix = fieldKey.toUpperCase().slice(0, 3);
    return `${prefix}-${Date.now().toString().slice(-6)}-${String(rowIndex + 1).padStart(4, '0')}`;
  };

  // Builds the same patched element set renderRowToHtml uses for the PDF,
  // but WITHOUT needing the final backend-assigned invoice number — a
  // placeholder is used, and the backend's own regex fixup in
  // create_invoice() swaps it for the real number once assigned. This lets
  // us send `elements` (and correctly-shaped `line_items`) at creation
  // time, instead of leaving the saved invoice record with none — which is
  // why the public "Scan to view invoice" page was rendering blank.
    const buildElementsForGroup = async (group: any, placeholderInvoiceNumber: string, translateTargetLang: string = '', showAmountInWordsParam: boolean = false) => {
    const get = (row: BatchRow, key: string) => {
      const col = mapping[key];
      return col ? (row.raw[col] || '').trim() : '';
    };
    const primaryRow = group.primaryRow;
    const clientTaxId = extractTaxIdFromRow(primaryRow.raw, mapping['client_tax_id']);
    const clientName = get(primaryRow, 'client_name');
    const clientAddress = get(primaryRow, 'client_address');
    const clientEmail = get(primaryRow, 'client_email');
    const clientPhone = get(primaryRow, 'client_phone');
    const todayDate = new Date().toISOString().split('T')[0]; // 👈 ADD THIS LINE

    const dynamicValues: Record<string, string> = {};
    dynamicFields.forEach(f => {
      let val = get(primaryRow, `dyn_${f.key}`);
      if (!val && AUTO_FALLBACK_FIELD_PATTERN.test(f.key)) {
        val = genFallback(f.key, primaryRow.rowIndex);
      }
      if (val) dynamicValues[f.key] = val;
    });
    customFields.forEach(f => {
      if (!f.column || !f.label.trim()) return;
      const val = (primaryRow.raw[f.column] || '').trim();
      if (val) dynamicValues[f.label.toLowerCase().replace(/\s+/g, '_')] = val;
    });

    const patched = selectedTemplate.elements.map(el => {
      // Populate the item table with REAL rows — this is what
      // PublicInvoiceView actually reads (tableEl.rows), not line_items.
      if (el.type === 'table') {
        // If we have a proper named breakdown (CGST/SGST/PPN/etc.), put it
        // ONLY on table.taxes and zero out each row's own taxRate — mirrors
        // the `hasTaxLines` branch already used for the PDF (renderRowToHtml).
        // CanvasWorkspace's computeTableTotals adds el.taxes AND each row's
        // taxRate together, so setting both double-counts the same tax.
        const hasNamedTaxLines = group.taxLines && group.taxLines.length > 0;
        return {
          ...el,
          rows: group.lineItems.map((li: any, i: number) => ({
            id: li.id || `li-${i}`,
            values: {
              description: li.description,
              qty: li.qty,
              rate: li.rate,
              taxRate: hasNamedTaxLines ? 0 : (li.taxRate || 0),
              taxInclusive: false,
            },
          })),
          taxes: hasNamedTaxLines
            ? group.taxLines.map((t: any) => ({
                label: t.label.replace(/\s*\([^)]*\)/g, '').replace(/\s*\d+(\.\d+)?%/g, '').trim(),
                rate: t.rate,
                amount: t.amount,
              }))
            : [],
          discounts: [],
          // Templates can carry a leftover legacy discountRate/taxRate from
          // whenever they were designed (single-rate fields, pre-dating the
          // taxes[]/discounts[] arrays). Spreading `...el` above copies those
          // stray values through unchanged. They're invisible in totals/PDF
          // (computeTableTotals only reads the arrays) but BillsView's
          // openEdit() falls back to discountRate/taxRate for legacy
          // invoices — so a template's old demo discount silently
          // resurfaces as a real, editable line the moment someone opens
          // Edit, and gets committed for real if they hit Update without
          // noticing.
          discountRate: 0,
          taxRate: 0,
        };
      }

      // Any QR already in the template belongs to the template (payment/website/etc.)
      // It must NEVER be tagged as the invoice QR so the backend does not overwrite it.
      if (el.type === 'qr') {
        el = {
          ...el,
          qrSource: 'template',
          _auto: undefined,
          id: String(el.id || '').startsWith('invoice-qr-') ? `template-qr-${primaryRow.rowIndex}` : el.id,
        };
      }

      if ((el as any).dynamicFieldKey && dynamicValues[(el as any).dynamicFieldKey] !== undefined) {
        const value = dynamicValues[(el as any).dynamicFieldKey];
        if (el.type === 'text') return { ...el, textContent: value };
        if (el.type === 'qr') return { ...el, qrValue: value };
        if (el.type === 'barcode') return { ...el, barcodeValue: value };
      }
      if (el.type !== 'text' || !el.textContent) return el;
      let t = el.textContent;

      // Clear demo Arabic from preset template
      delete (el as any).arabicContent;

      // Replace demo "Bill To" block with the real client info — do this
      // FIRST and BEFORE the invoice-number regex below, since "bill to"
      // starting with "bill" would otherwise get corrupted by it.
      if (/bill\s*to\s*:?/i.test(t)) {
        const taxLine = clientTaxId ? (clientTaxId.length === 15 ? `GSTIN: ${clientTaxId}` : `Tax ID: ${clientTaxId}`) : '';
        const infoLines = [
          clientName,
          clientAddress,
          taxLine,
          clientEmail,
          clientPhone,
        ].filter(Boolean);
        t = t.replace(/(bill\s*to\s*:?\s*\n?)([\s\S]*)/i, (_: any, heading: string) => `${heading}${infoLines.join('\n')}`);
      }

      // Clean universal placeholders swap
      t = t
        .replace(/\{invoice_number\}/g, placeholderInvoiceNumber)
        .replace(/\{date\}/g, todayDate)
        .replace(/\{client_name\}/g, clientName || '')
        .replace(/\{client_address\}/g, clientAddress || '');

      for (const [key, value] of Object.entries(dynamicValues)) {
        if (!value) continue;
        const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#]*');
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_: any, l: string) => `${l}: ${value}`);
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*_{2,}`, 'gi'), (_: any, l: string) => `${l}: ${value}`);
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*[-—]{1,3}`, 'gi'), (_: any, l: string) => `${l}: ${value}`);
        t = t.replace(
          new RegExp(`(${labelPattern})\\s*:\\s*([^\\n:]{0,50}?)(?=\\s{2,}[A-Za-z]|\\n|$)`, 'gi'),
          (_: any, l: string) => `${l}: ${value}`
        );
      }
      if (clientTaxId && /bill\s*to\s*:/i.test(t) && !t.includes(clientTaxId)) {
        t = `${t}\nTax ID / GSTIN: ${clientTaxId}`;
      }
      return { ...el, textContent: t };
    });

    const elementsWithWords = patched;

    // Bilingual pass — mirrors the translation logic already used for the
    // PDF (renderRowToHtml's isBilingual branch), but applied to the
    // ELEMENTS sent to the backend so the public/scanned page can render
    // BilingualInvoiceRenderer too, not just the downloaded PDF.
    const cleanBaseElements = elementsWithWords.filter(
      (el: any) => !(el.type === 'qr' && (el._auto === 'invoice' || String(el.id || '').startsWith('invoice-qr'))) &&
                   !(el.type === 'text' && (el._auto === 'invoice_label' || String(el.id || '').startsWith('invoice-label')))
    );

    // Build the positioned invoice QR + label the same way renderRowToHtml
    // does for the PDF — without this, the saved `elements` sent to the
    // backend never carry qrAlign/qrPosition info at all, so the scanned
    // public page always falls back to whatever default the backend uses
    // (center), regardless of what was picked in the batch UI.
    let qrElementsToAdd: any[] = [];
    if (includeQr) {
      const LEFT_MARGIN = 60;
      const pc = PAGE_CONFIGS[selectedTemplate.pageSize || 'A4'] || PAGE_CONFIGS.A4;
      const isThermalTpl = (selectedTemplate.pageSize || '').startsWith('THERMAL_');
      const pageW = isThermalTpl ? pc.width : 794;
      const qrSizeDefault = isThermalTpl ? 80 : 100;
      const templateQr = cleanBaseElements.find((el: any) => el.type === 'qr');
      const matchedQrSize = templateQr?.width || qrSizeDefault;

      let qrX: number;
      if (qrPosition) {
        qrX = qrPosition.x;
      } else {
        qrX = qrAlign === 'left' ? LEFT_MARGIN
          : qrAlign === 'right' ? pageW - matchedQrSize - LEFT_MARGIN
          : (pageW - matchedQrSize) / 2;
      }

      const tableEl = cleanBaseElements.find((el: any) => el.type === 'table');
      const tableBottom = tableEl ? (tableEl.y || 0) + (tableEl.height || 120) : 0;
      const maxContentY = cleanBaseElements.reduce((max: number, el: any) => {
        if (el.type === 'shape' && (el.height || 0) >= (pc.height || 1123) * 0.7) return max;
        let elHeight = el.height || 30;
        if (el.type === 'text' && el.textContent) {
          const lines = el.textContent.split('\n');
          const approxCharsPerLine = Math.max(20, Math.floor((el.width || 300) / 6.5));
          const totalLines = lines.reduce((acc: number, line: string) => {
            return acc + Math.max(1, Math.ceil(line.length / approxCharsPerLine));
          }, 0);
          elHeight = Math.max(elHeight, totalLines * ((el.fontSize || 9.5) * 1.45 + 3));
        }
        return Math.max(max, (el.y || 0) + elHeight);
      }, tableBottom);

      const qrY = qrPosition
        ? qrPosition.y
        : maxContentY + 24;

      const labelY = qrY + matchedQrSize + 4;
      const labelWidth = Math.max(matchedQrSize + 40, 140);
      const labelX = Math.max(0, qrX + (matchedQrSize - labelWidth) / 2);

      qrElementsToAdd = [
        {
          id: `invoice-qr-${group.primaryRow.rowIndex}`,
          type: 'qr',
          _auto: 'invoice',
          qrSource: 'invoice',
          qrValue: placeholderInvoiceNumber,
          x: qrX, y: qrY, width: matchedQrSize, height: matchedQrSize, zIndex: 99, color: '#000000',
        },
        {
          id: `invoice-label-${group.primaryRow.rowIndex}`,
          type: 'text',
          _auto: 'invoice_label',
          textContent: 'Scan to view invoice',
          x: labelX, y: labelY, width: labelWidth, height: 18,
          fontSize: 9, fontFamily: 'Inter, sans-serif', textAlign: 'center',
          color: '#374151', fontWeight: '700', zIndex: 99,
        },
      ];
    }

    if (translateTargetLang && translateTargetLang !== 'en') {
      // Collect ALL text lines, column headers, and item descriptions into 1 array
      const allBatchTexts: string[] = [];
      [...cleanBaseElements, ...qrElementsToAdd].forEach((el: any) => {
        if (el.type === 'text' && el.textContent?.trim()) {
          el.textContent.split('\n').forEach((l: string) => l.trim() && allBatchTexts.push(l.trim()));
        }
        if (el.type === 'table') {
          (el.columns || []).forEach((c: any) => c.header && allBatchTexts.push(c.header));
          (el.rows || []).forEach((r: any) => r.values?.description && allBatchTexts.push(r.values.description));
        }
      });
      // Pre-translate everything for this invoice in 1 SINGLE API CALL
      await preloadBatchTranslations(allBatchTexts, translateTargetLang);

      const translatedEls = await Promise.all(
        [...cleanBaseElements, ...qrElementsToAdd].map(async (el: any) => {
          if (el.type === 'text' && el.textContent?.trim()) {
            const lines = el.textContent.split('\n');
            const translatedLines = await Promise.all(lines.map((l: string) => translateCached(l, translateTargetLang)));
            return { ...el, arabicContent: translatedLines.join('\n'), translatedLang: translateTargetLang };
          }
          if (el.type === 'table') {
            const translatedCols = await Promise.all(
              (el.columns || []).map(async (col: any) => ({
                ...col,
                arabicHeader: await translateCached(col.header, translateTargetLang),
              }))
            );
            const translatedRows = await Promise.all(
              (el.rows || []).map(async (row: any) => ({
                ...row,
                values: {
                  ...row.values,
                  arabicName: await translateCached(row.values?.description || '', translateTargetLang),
                },
              }))
            );
            return { ...el, columns: translatedCols, rows: translatedRows };
          }
          return el;
        })
      );
      return translatedEls;
    }

    return [...cleanBaseElements, ...qrElementsToAdd];
  };

  const handleGenerate = async () => {
    if (invoiceGroups.length === 0) return alert('No valid invoices to generate.');
    
    // Apply edits to csvRows
    const updatedRows = csvRows.map((row, i) => {
      if (editedRows[i]) {
        return { ...row, ...editedRows[i] };
      }
      return row;
    });
    setCsvRows(updatedRows);
    
    setStage('generating');
    cancelRef.current = false;
    setCancelled(false);
    setRowErrors([]);

    // 🚀 GLOBAL 1-CALL BATCH PRELOAD: Translates the ENTIRE CSV in 1 single request!
    if (batchTranslate && batchLang && batchLang !== 'en') {
      setProgress({ stage: 'Translating entire batch in 1 request...', current: 0, total: 1 });
      const allUniqueBatchTexts = new Set<string>();
      allUniqueBatchTexts.add('Scan to view invoice');

      // 1. Collect all unique line items across every invoice in the CSV
      invoiceGroups.forEach(g => {
        g.lineItems.forEach((li: any) => {
          if (li.description?.trim()) allUniqueBatchTexts.add(li.description.trim());
        });
      });

      // 2. Collect all template text elements & column headers
      (selectedTemplate.elements || []).forEach((el: any) => {
        if (el.type === 'text' && el.textContent?.trim()) {
          el.textContent.split('\n').forEach((l: string) => l.trim() && allUniqueBatchTexts.add(l.trim()));
        }
        if (el.type === 'table') {
          (el.columns || []).forEach((c: any) => c.header && allUniqueBatchTexts.add(c.header));
        }
      });

      // 3. Send ALL items across all 100 invoices in ONE single API call!
      await preloadBatchTranslations(Array.from(allUniqueBatchTexts), batchLang);
    }

    // ---------- Create real invoice records first ----------
    setProgress({ stage: 'Creating invoice records', current: 0, total: invoiceGroups.length });
    const { invoicesApi } = await import('../utils/api');
    const invoiceMeta = new Map<number, { id: string; number: string; qrUrl: string }>();
    const createErrors: { rowIndex: number; error: string }[] = [];
    let createdCount = 0;

    const getField = (row: BatchRow, key: string) => {
      const col = mapping[key];
      const edited = editedRows[row.rowIndex] || {};
      const source = { ...row.raw, ...edited };
      return col ? (source[col] || '').trim() : '';
    };

    await runWithConcurrency(invoiceGroups, 8, async (group) => {
      if (cancelRef.current) return;
      try {
        const placeholderNum = (group.customInvoiceNum || `PEND-${String(group.groupIndex + 1).padStart(4, '0')}`).toUpperCase();
        const translateLang = batchTranslate ? batchLang : '';

        const saved = await invoicesApi.create({
          client_name: getField(group.primaryRow, 'client_name'),
          client_email: getField(group.primaryRow, 'client_email'),
          client_phone: getField(group.primaryRow, 'client_phone'),
          client_address: getField(group.primaryRow, 'client_address'),
          currency: selectedCurrency,
          amount: group.totalAmount,
          page_size: selectedTemplate.pageSize || 'A4',
          // Backend LineItem schema wants unit_price/amount, not rate/total.
          line_items: group.lineItems.map((i: any) => ({
            description: i.description,
            qty: i.qty,
            unit_price: i.rate,
            amount: i.qty * i.rate,
            tax_rate: i.taxRate || 0,
            tax_inclusive: false,
            tax_amount: i.taxAmount || 0,
          })),
          // Without this, invoice.elements stays empty server-side and the
          // public "Scan to view invoice" page renders blank.
          elements: await buildElementsForGroup(group, placeholderNum, translateLang, showAmountInWords),
          // These two were missing — the backend auto-injects the invoice
          // QR itself (independent of `elements`) and was always defaulting
          // to center/always-shown, ignoring the left/center/right choice
          // and the "Include QR" toggle picked in the batch UI. This is why
          // the scanned web page's QR position never matched the PDF's.
          qr_position: qrAlign,
          show_invoice_qr: includeQr,
          // Without this, PublicInvoiceView.tsx's `isBilingual` check always
          // reads false, so the public page always renders the plain
          // English layout regardless of the "Translate every document"
          // toggle — even though the downloaded PDF was correctly bilingual.
          is_bilingual: !!translateLang,
          show_amount_in_words: showAmountInWords,
          document_type: 'invoice',
          generation_source: 'batch',
          outlet_id: outletId ?? null,
        } as any);
        // Primary source: the backend echoes back the invoice's own
        // elements, including the QR it auto-injected and tagged
        // _auto:'invoice' with the real, already-correct qrValue — this
        // is the approach that was actually working (confirmed against
        // the last known-good build). Building the URL ourselves from
        // saved.public_token/etc as a replacement for this regressed
        // silently: if none of those fields are present on the response,
        // qrUrl ends up empty, which skips QR injection in
        // renderRowToHtml entirely, leaving any leftover placeholder QR
        // baked into the template untouched in the final PDF.
        // Always build the public viewing URL directly from the saved invoice token/id
        const token = saved.public_token || saved.publicToken || saved.token || saved.invoice?.public_token || saved.id || '';
        const resolvedQrUrl = token
          ? (String(token).startsWith('http') ? String(token) : `${window.location.origin}/invoice/view/${token}`)
          : `${window.location.origin}/invoice/view/${saved.id || placeholderNum}`;

        invoiceMeta.set(group.groupIndex, {
          id: String(saved.id || ''),
          number: saved.invoice_number || group.customInvoiceNum,
          qrUrl: resolvedQrUrl,
        });
      } catch (e: any) {
        const detail = e?.response?.data?.detail || e?.message || e;
        const msg = typeof detail === 'object' ? JSON.stringify(detail) : String(detail);
        createErrors.push({ rowIndex: group.primaryRow.rowIndex, error: `Invoice creation failed: ${msg}` });
      }
      createdCount++;
      setProgress({ stage: 'Creating invoice records', current: createdCount, total: invoiceGroups.length });
    });

    if (cancelRef.current) { setStage('review'); return; }

    if (createErrors.length) setRowErrors(createErrors);
    const renderableGroups = invoiceGroups.filter(g => invoiceMeta.has(g.groupIndex));
    if (renderableGroups.length === 0) {
      alert('No invoice records could be created — check the errors below.');
      setStage('review');
      return;
    }

    const pc = PAGE_CONFIGS[selectedTemplate.pageSize || 'A4'] || PAGE_CONFIGS.A4;

    setProgress({ stage: 'Rendering & generating PDFs', current: 0, total: renderableGroups.length });

    const pageCss = await getPageCss();
    const isThermal = (selectedTemplate.pageSize || '').startsWith('THERMAL_');
    const pageWidth = pc.width;
    const pageHeight = isThermal ? 1200 : pc.height;

    const pdfBuffers: ArrayBuffer[] = [];
    const localErrors: { rowIndex: number; error: string }[] = [];
    let processedCount = 0;

    // Generate each invoice as its own single-page A4 document client-side
    await runWithConcurrency<any, void>(renderableGroups as any[], 2, async (group: any, idx) => {
      if (cancelRef.current) return;
      try {
        const meta = invoiceMeta.get(group.groupIndex)!;
        const rowHtml = await renderRowToHtml(
          group.primaryRow, selectedTemplate, mapping, dynamicFields, genFallback, selectedCurrency,
          meta.number, includeQr ? meta.qrUrl : '', qrPosition, customFields, dayFirstFields, qrAlign,
          batchTranslate ? batchLang : '', group.lineItems, group.taxLines,
          showAmountInWords, group.totalAmount, currencyDisplayMode
        );

        // 100% Client-side generation (No server dependency)
        const pdfBuffer = await generatePdfClientSide(
          rowHtml,
          pageCss,
          pageWidth,
          pageHeight,
          isThermal,
          meta.number
        );
        pdfBuffers[idx] = pdfBuffer;
      } catch (e: any) {
        console.error('Invoice render error:', e);
        localErrors.push({ rowIndex: group.primaryRow.rowIndex, error: `PDF generation failed: ${e?.message || e}` });
      }
      processedCount++;
      setProgress({ stage: 'Rendering & generating PDFs', current: processedCount, total: renderableGroups.length });
    });

    setRowErrors(localErrors);

    if (cancelRef.current) {
      setStage('review');
      return;
    }

    const validBuffers = pdfBuffers.filter(Boolean);
    if (validBuffers.length === 0) {
      alert('No PDFs were generated — check the errors below.');
      setStage('review');
      return;
    }

    // ---------- Merge PDFs client-side into one file ----------
    setProgress({ stage: 'Merging into one PDF', current: 0, total: validBuffers.length });
    try {
      const merged = await PDFDocument.create();
      for (let i = 0; i < validBuffers.length; i++) {
        const src = await PDFDocument.load(validBuffers[i]);
        const pages = await merged.copyPages(src, src.getPageIndices());
        pages.forEach(p => merged.addPage(p));
        setProgress({ stage: 'Merging into one PDF', current: i + 1, total: validBuffers.length });
      }
      const mergedBytes = await merged.save();
      const finalBlob = new Blob([mergedBytes], { type: 'application/pdf' });
      setResultBlob(finalBlob);
      onAddLog('BATCH_GENERATED', `Batch generated ${renderableGroups.length - localErrors.length}/${renderableGroups.length} invoices from ${fileName} using ${selectedTemplate.name}`);
      postLog('BATCH_GENERATED', `Batch: ${fileName} → ${renderableGroups.length - localErrors.length} invoices (${selectedTemplate.name})`);
      setStage('done');
    } catch (e: any) {
      alert(`Merge failed: ${e?.message}`);
      setStage('review');
    }
  };
  const cancelGeneration = () => {
    cancelRef.current = true;
    setCancelled(true);
  };

  const downloadResult = async () => {
  if (!resultBlob) return;

  const fileName =
    `batch_${selectedTemplate.name.replace(/\s+/g, '_')}_${Date.now()}.pdf`;

  // Mobile app WebView:
  // send the PDF to React Native instead of browser downloading it.
  if (
    isMobileBatchEmbed &&
    (window as any).ReactNativeWebView
  ) {
    const reader = new FileReader();

    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];

      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'batch-pdf-download',
          fileName,
          base64,
        })
      );
    };

    reader.readAsDataURL(resultBlob);
    return;
  }

  // Normal website download
  const url = URL.createObjectURL(resultBlob);
  const a = document.createElement('a');

  a.href = url;
  a.download = fileName;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

  const resetAll = () => {
    setStage('template');
    setCsvHeaders([]); setCsvRows([]); setFileName('');
    setMapping({}); setRowErrors([]); setResultBlob(null);
  };

  // ============================== UI ==============================
  return (
    <div
  style={{
    width: '100%',
    maxWidth: isMobileBatchEmbed ? '100%' : 900,
    margin: '0 auto',
    padding: isMobileBatchEmbed ? '12px 10px 24px' : 24,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  }}
>
      {usage && usage.limit !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 10px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {usage.count}/{usage.limit} invoices this month
          </div>
          <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, Math.round((usage.count / usage.limit) * 100))}%`,
              height: '100%',
              background: usage.count >= usage.limit ? '#ef4444' : (usage.count / usage.limit) >= 0.8 ? '#f59e0b' : '#22c55e',
              borderRadius: 99, transition: 'width 0.3s',
            }} />
          </div>
        </div>
      )}
      {usage && usage.limit === null && (
        <div style={{ marginBottom: 14, padding: '6px 10px', borderRadius: 7, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 11, fontWeight: 700, color: '#16a34a' }}>
          ✓ Unlimited invoices
        </div>
      )}
      <StageHeader
  stage={stage}
  mobile={isMobileBatchEmbed}
/>

      {stage === 'template' && (
        <div style={card}>
          <h3 style={h3}>1. Pick a template</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
              <optgroup label="Ready-Made Presets">
                {TEMPLATE_PRESETS.map(t => <option key={t.id || t.name} value={t.id || t.name}>{t.name}</option>)}
              </optgroup>
              {customTemplates.length > 0 && (
                <optgroup label="My Templates">
                  {customTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              )}
            </select>
            <button
              onClick={() => {
                const isDefault = defaultTemplateId === selectedTemplateId;
                if (isDefault) {
                  localStorage.removeItem('batch_default_template_id');
                  setDefaultTemplateId('');
                } else {
                  localStorage.setItem('batch_default_template_id', selectedTemplateId);
                  setDefaultTemplateId(selectedTemplateId);
                }
              }}
              title={defaultTemplateId === selectedTemplateId ? 'Remove as default' : 'Set as default template'}
              style={{
                background: defaultTemplateId === selectedTemplateId ? '#fef3c7' : '#f8fafc',
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '0 12px', cursor: 'pointer', fontSize: 16,
              }}
            >
              {defaultTemplateId === selectedTemplateId ? '⭐' : '☆'}
            </button>
          </div>
          {selectedTemplate && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
              Detected {dynamicFields.length} custom field{dynamicFields.length !== 1 ? 's' : ''} in this template
              {dynamicFields.length > 0 && <>: {dynamicFields.map(f => f.label).join(', ')}</>}
            </div>
          )}
          <button onClick={() => setStage('upload')} style={primaryBtn}>
            Next: Upload CSV <ArrowRight size={14} />
          </button>
        </div>
      )}

      {stage === 'upload' && (
        <div style={card}>
          <h3 style={h3}>2. Upload your data</h3>
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            style={{
  ...dropZone,
  padding: isMobileBatchEmbed ? '28px 14px' : dropZone.padding,
  minHeight: isMobileBatchEmbed ? 180 : undefined,
}}
          >
            <Upload size={28} color="#9ca3af" />
            <p style={{ margin: '10px 0 4px', fontSize: 13, color: '#374151', fontWeight: 600 }}>{isMobileBatchEmbed
  ? 'Tap to choose CSV or Excel file'
  : 'Drop CSV or Excel file here or click to browse'}</p>
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>Headers can be anything — we'll auto-match columns to fields</p>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
          </div>
          <button onClick={() => setStage('template')} style={secondaryBtn}><ArrowLeft size={14} /> Back</button>
        </div>
      )}

      {stage === 'mapping' && (
        <div style={card}>
          <h3 style={h3}>3. Confirm column mapping</h3>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: -6, marginBottom: 14 }}>
            {fileName} · {csvRows.length} rows · auto-matched where possible
          </p>

          {/* Tax & Calculation Mode Selector */}
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14,
            padding: '12px 14px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
              Tax & Totals Calculation Mode:
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobileBatchEmbed ? '1fr' : '1fr 1fr 1fr',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={() => setTaxMode('exact_file')}
                style={{
                  padding: '9px 10px', borderRadius: 8, border: '1.5px solid',
                  borderColor: taxMode === 'exact_file' ? '#3b82f6' : '#cbd5e1',
                  background: taxMode === 'exact_file' ? '#eff6ff' : '#fff',
                  color: taxMode === 'exact_file' ? '#1d4ed8' : '#475569',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                }}
              >
                📄 Exact from File
                <div style={{ fontSize: 9.5, fontWeight: 500, color: '#64748b', marginTop: 2 }}>
                  Uses exact Subtotal, Tax & Total from file. Disables Country Scheme.
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTaxMode('file_amounts_country_name')}
                style={{
                  padding: '9px 10px', borderRadius: 8, border: '1.5px solid',
                  borderColor: taxMode === 'file_amounts_country_name' ? '#3b82f6' : '#cbd5e1',
                  background: taxMode === 'file_amounts_country_name' ? '#eff6ff' : '#fff',
                  color: taxMode === 'file_amounts_country_name' ? '#1d4ed8' : '#475569',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                }}
              >
                🌍 File Totals + Country Name
                <div style={{ fontSize: 9.5, fontWeight: 500, color: '#64748b', marginTop: 2 }}>
                  Keeps file numbers, but renames Tax to selected Country (VAT/GST).
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTaxMode('auto_calculate')}
                style={{
                  padding: '9px 10px', borderRadius: 8, border: '1.5px solid',
                  borderColor: taxMode === 'auto_calculate' ? '#3b82f6' : '#cbd5e1',
                  background: taxMode === 'auto_calculate' ? '#eff6ff' : '#fff',
                  color: taxMode === 'auto_calculate' ? '#1d4ed8' : '#475569',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                }}
              >
                ⚡ Auto-Calculate All
                <div style={{ fontSize: 9.5, fontWeight: 500, color: '#64748b', marginTop: 2 }}>
                  Calculates Qty × Rate and State/Country Tax Breakdowns.
                </div>
              </button>
            </div>
          </div>

          {/* Business Country, StatePicker & Tax Scheme Card (from Product Catalog) */}
          <div style={{
            display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16,
            padding: '12px 16px', background: taxMode === 'exact_file' ? '#f1f5f9' : '#fff',
            border: '1.5px solid #e2e8f0', borderRadius: 12,
            opacity: taxMode === 'exact_file' ? 0.65 : 1,
            pointerEvents: taxMode === 'exact_file' ? 'none' : 'auto',
            boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
          }}>
            {taxMode === 'exact_file' && (
              <div style={{ width: '100%', fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                🔒 Business Country Scheme is disabled because &quot;Exact from File&quot; mode is active.
              </div>
            )}
            {/* Country Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7, background: '#eef2ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: 14 }}>🌍</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Business Country
              </span>
            </div>

            {/* Country Selector (Full World List) */}
            <select
              value={sellerCountry}
              onChange={e => handleCountryChange(e.target.value)}
              style={{
                padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12,
                fontWeight: 700, color: '#0f172a', background: '#f8fafc', cursor: 'pointer', outline: 'none',
                minWidth: 160,
              }}
            >
              <option value="">Select country…</option>
              {getOrderedCountryList().map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>

            {/* Currency Selector (All Global Currencies with Symbols) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>Currency:</span>
              <select
                value={selectedCurrency}
                onChange={e => setSelectedCurrency(e.target.value)}
                style={{
                  padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11,
                  fontWeight: 700, color: '#1e293b', background: '#fff', cursor: 'pointer', outline: 'none',
                  maxWidth: 220,
                }}
              >
                {Object.entries(CURRENCY_MAP).map(([code, name]) => {
                  const sym = getCurrencySymbol(code);
                  return (
                    <option key={code} value={code}>
                      {code} ({sym}) — {name}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Currency Symbol vs 3-Letter Code Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', padding: '3px', borderRadius: 8, border: '1px solid #cbd5e1', gap: 2 }}>
              <button
                type="button"
                onClick={() => setCurrencyDisplayMode('symbol')}
                style={{
                  padding: '4px 9px', borderRadius: 6, border: 'none',
                  background: currencyDisplayMode === 'symbol' ? '#3b82f6' : 'transparent',
                  color: currencyDisplayMode === 'symbol' ? '#fff' : '#475569',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s ease',
                }}
              >
                {getCurrencySymbol(selectedCurrency)} Symbol
              </button>
              <button
                type="button"
                onClick={() => setCurrencyDisplayMode('code')}
                style={{
                  padding: '4px 9px', borderRadius: 6, border: 'none',
                  background: currencyDisplayMode === 'code' ? '#3b82f6' : 'transparent',
                  color: currencyDisplayMode === 'code' ? '#fff' : '#475569',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s ease',
                }}
              >
                {selectedCurrency.toUpperCase()} Code
              </button>
            </div>

            {/* StatePicker (Only appears for countries with states/provinces like India, US, Canada, Australia) */}
            {(!!taxScheme.region_based || REGIONAL_TAX_COUNTRIES.has(sellerCountry)) && (
              <StatePicker
                countryCode={sellerCountry}
                value={sellerRegion}
                onChange={r => {
                  setSellerRegion(r);
                  setSelectedSchemeId(null);
                }}
                placeholder="Seller State / Province"
              />
            )}

            <div style={{ width: 1, height: 22, background: '#e2e8f0' }} />

            {/* Tax Scheme Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>Tax Scheme:</span>
              <select
                value={taxScheme.id}
                onChange={e => setSelectedSchemeId(e.target.value)}
                style={{
                  padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11,
                  fontWeight: 700, color: '#1e293b', background: '#fff', cursor: 'pointer', outline: 'none',
                }}
              >
                {availableSchemes.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Component Badges (e.g. • CGST: 9%  • SGST: 9%) */}
            {taxScheme.components && taxScheme.components.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {taxScheme.components.map((comp, i) => (
                  <span key={i} style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>
                    • {comp.name}: <strong>{comp.rate}%</strong>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
            {allFields.map(f => {
              const isDateField = f.key === 'invoice_date' || f.key === 'due_date';
              return (
              <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
  style={{
    display: 'grid',
    gridTemplateColumns: isMobileBatchEmbed
      ? '1fr'
      : '1fr 1fr',
    gap: isMobileBatchEmbed ? 6 : 10,
    alignItems: 'center',
  }}
>
                  <div style={{ fontSize: 12, fontWeight: 600, color: REQUIRED_FIELDS.has(f.key) ? '#dc2626' : '#374151' }}>
                    {f.label} {REQUIRED_FIELDS.has(f.key) && '*'}
                  </div>
                  <select value={mapping[f.key] || ''} onChange={e => updateMapping(f.key, e.target.value || null)} style={selectStyle}>
                    <option value="">— Not mapped —</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                {isDateField && mapping[f.key] && (
                  <div style={{
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: isMobileBatchEmbed ? 0 : 'auto',
  width: isMobileBatchEmbed ? '100%' : '50%',
  flexWrap: 'wrap',
}}>
                    <span style={{ fontSize: 10, color: '#6b7280', background: '#f1f5f9', padding: '3px 8px', borderRadius: 99, fontWeight: 600 }}>
                      Detected: {dayFirstFields[f.key] ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
                    </span>
                    <button
                      onClick={() => setDayFirstFields(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                      style={{ fontSize: 10, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}
                    >
                      Switch to {dayFirstFields[f.key] ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                    </button>
                  </div>
                )}
              </div>
              );
            })}

            {customFields.map(f => (
              <div
  key={f.id}
  style={{
    display: 'grid',
    gridTemplateColumns: isMobileBatchEmbed
      ? '1fr'
      : '1fr 1fr auto',
    gap: isMobileBatchEmbed ? 6 : 10,
    alignItems: 'center',
  }}
>
                <input
                  value={f.label}
                  onChange={e => setCustomFields(prev => prev.map(cf => cf.id === f.id ? { ...cf, label: e.target.value } : cf))}
                  placeholder="Custom field name"
                  style={{ ...selectStyle, fontWeight: 600 }}
                />
                <select
                  value={f.column || ''}
                  onChange={e => setCustomFields(prev => prev.map(cf => cf.id === f.id ? { ...cf, column: e.target.value || null } : cf))}
                  style={selectStyle}
                >
                  <option value="">— Not mapped —</option>
                  {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <button
                  onClick={() => setCustomFields(prev => prev.filter(cf => cf.id !== f.id))}
                  style={{ ...secondaryBtn, marginTop: 0, padding: '6px 10px' }}
                >✕</button>
              </div>
            ))}
            <button
              onClick={() => setCustomFields(prev => [...prev, { id: `cf_${Date.now()}`, label: '', column: null }])}
              style={{ ...secondaryBtn, marginTop: 4 }}
            >+ Add custom field</button>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button onClick={() => setStage('upload')} style={secondaryBtn}><ArrowLeft size={14} /> Back</button>
            <button onClick={() => setStage('editing')} style={primaryBtn}>Next: Edit rows <ArrowRight size={14} /></button>
          </div>
        </div>
      )}

      {stage === 'editing' && (
        <div style={card}>
          <h3 style={h3}>4. Edit rows (optional)</h3>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Click any cell to add or change data. Leave blank to auto-generate.
          </p>

          {csvRows.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
              No rows to edit — go back and upload a CSV/Excel file.
            </div>
          ) : (
          <div
  style={{
    maxHeight: isMobileBatchEmbed ? 480 : 400,
    overflowY: 'auto',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    marginBottom: 14,
    width: '100%',
  }}
>
            <table
  style={{
    width: '100%',
    minWidth: isMobileBatchEmbed
      ? Math.max(600, csvHeaders.length * 150)
      : undefined,
    fontSize: isMobileBatchEmbed ? 12 : 11,
    borderCollapse: 'collapse',
  }}
>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ padding: 8, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb', width: 40 }}>Row</th>
                  {csvHeaders.map(header => (
  <th key={header} style={{ padding: 8, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
    {header}
  </th>
))}
                </tr>
              </thead>
              <tbody>
                {validatedRows.map(row => {
                  const edited = editedRows[row.rowIndex] || {};
                  return (
                    <tr key={row.rowIndex} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 6, background: '#f8fafc', fontWeight: 600, fontSize: 10, color: '#6b7280' }}>
                        {row.rowIndex + 2}
                      </td>
                      {csvHeaders.map(header => {
                        const originalVal = row.raw[header] || '';
                        const currentVal = edited[header] !== undefined ? edited[header] : originalVal;
                        return (
                          <td key={header} style={{ padding: 4 }}>
                            <input
                              type="text"
                              value={currentVal}
                              onChange={(e) => {
                                setEditedRows(prev => ({
                                  ...prev,
                                  [row.rowIndex]: { ...prev[row.rowIndex], [header]: e.target.value }
                                }));
                              }}
                              placeholder={originalVal || 'auto'}
                              style={{
  width: '100%',
  minWidth: isMobileBatchEmbed ? 130 : undefined,
  padding: isMobileBatchEmbed ? '8px 8px' : '4px 6px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: isMobileBatchEmbed ? 14 : 11,
  background:
    edited[header] !== undefined ? '#eff6ff' : '#fff',
  boxSizing: 'border-box',
}}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStage('mapping')} style={secondaryBtn}><ArrowLeft size={14} /> Back</button>
            <button onClick={() => setStage('review')} style={primaryBtn}>Next: Review <ArrowRight size={14} /></button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <div style={card}>
          <h3 style={h3}>4. Review before generating</h3>

          {usage && (() => {
            const planLabel = usage.plan ? usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1) : 'Your';

            if (usage.limit === null) {
              return (
                <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                  ✓ {planLabel} Plan · Unlimited invoices
                </div>
              );
            }

            const remaining = Math.max(0, usage.limit - usage.count);
            const wouldExceed = invoiceGroups.length > remaining;
            return (
              <div style={{
                marginBottom: 14, padding: '10px 12px', borderRadius: 8,
                background: wouldExceed ? '#fef2f2' : '#f0f9ff',
                border: `1px solid ${wouldExceed ? '#fecaca' : '#bae6fd'}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: wouldExceed ? '#dc2626' : '#0369a1' }}>
                  {planLabel} Plan · {usage.count}/{usage.limit} invoices used this month
                </div>
                {wouldExceed ? (
                  <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
                    ⚠️ This batch will produce {invoiceGroups.length} invoice{invoiceGroups.length !== 1 ? 's' : ''}, but only {remaining} remain on your plan this month.
                    The first {remaining} will generate; the rest will fail.
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: '#0c4a6e', marginTop: 4 }}>
                    {remaining} invoice{remaining !== 1 ? 's' : ''} remaining · resets {usage.reset_date}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
            <StatBox label="Total rows" value={csvRows.length} />
            <StatBox label="Invoices to make" value={invoiceGroups.length} color="#3b82f6" />
            <StatBox label="Valid rows" value={validRows.length} color="#16a34a" />
            <StatBox label="Errors" value={errorRowCount} color={errorRowCount > 0 ? '#dc2626' : '#9ca3af'} />
          </div>

          {errorRowCount > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10, marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
              {validatedRows.filter(r => r.errors.length > 0).slice(0, 30).map(r => (
                <div key={r.rowIndex} style={{ fontSize: 11, color: '#991b1b', marginBottom: 3 }}>
                  <AlertTriangle size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                  Row {r.rowIndex + 2}: {r.errors.join('; ')}
                </div>
              ))}
              {errorRowCount > 30 && <div style={{ fontSize: 11, color: '#991b1b' }}>...and {errorRowCount - 30} more</div>}
            </div>
          )}

          {rowErrors.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Errors from last generation attempt:</div>
              {rowErrors.slice(0, 30).map((r, i) => (
                <div key={i} style={{ fontSize: 11, color: '#92400e' }}>Row {r.rowIndex + 2}: {r.error}</div>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
            Will generate {invoiceGroups.length} document{invoiceGroups.length !== 1 ? 's' : ''} on {selectedTemplate.name},
            merged into one PDF ({Math.ceil(invoiceGroups.length / CHUNK_SIZE)} batch{Math.ceil(invoiceGroups.length / CHUNK_SIZE) !== 1 ? 'es' : ''} of up to {CHUNK_SIZE} invoices each).
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeQr} onChange={e => setIncludeQr(e.target.checked)} />
            Include a "Scan to view invoice" QR code on each document
          </label>

          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', cursor: 'pointer', marginBottom: batchTranslate ? 8 : 0 }}>
              <input type="checkbox" checked={batchTranslate} onChange={e => setBatchTranslate(e.target.checked)} />
              Translate every document (bilingual — English + selected language)
            </label>
            {batchTranslate && (
              <>
                <select value={batchLang} onChange={e => setBatchLang(e.target.value)} style={{ ...selectStyle, maxWidth: 220 }}>
                  {[
                    ['af','Afrikaans'],['sq','Albanian'],['am','Amharic'],['ar','Arabic'],['hy','Armenian'],['az','Azerbaijani'],
                    ['eu','Basque'],['be','Belarusian'],['bn','Bengali'],['bs','Bosnian'],['bg','Bulgarian'],['ca','Catalan'],['ceb','Cebuano'],
                    ['ny','Chichewa'],['zh','Chinese (Simplified)'],['zh-TW','Chinese (Traditional)'],['co','Corsican'],
                    ['hr','Croatian'],['cs','Czech'],['da','Danish'],['nl','Dutch'],['eo','Esperanto'],['et','Estonian'],['tl','Filipino'],                    ['fi','Finnish'],['fr','French'],['fy','Frisian'],['gl','Galician'],['ka','Georgian'],                    ['de','German'],['el','Greek'],['gu','Gujarati'],['ht','Haitian Creole'],['ha','Hausa'],
                    ['haw','Hawaiian'],['iw','Hebrew'],['hi','Hindi'],['hmn','Hmong'],['hu','Hungarian'],
                    ['is','Icelandic'],['ig','Igbo'],['id','Indonesian'],['ga','Irish'],['it','Italian'],
                    ['ja','Japanese'],['jw','Javanese'],['kn','Kannada'],['kk','Kazakh'],['km','Khmer'],
                    ['rw','Kinyarwanda'],['ko','Korean'],['ku','Kurdish (Kurmanji)'],['ky','Kyrgyz'],['lo','Lao'],
                    ['la','Latin'],['lv','Latvian'],['lt','Lithuanian'],['lb','Luxembourgish'],['mk','Macedonian'],
                    ['mg','Malagasy'],['ms','Malay'],['ml','Malayalam'],['mt','Maltese'],['mi','Maori'],                    
                    ['mr','Marathi'],['mn','Mongolian'],['my','Myanmar (Burmese)'],['ne','Nepali'],['no','Norwegian'],                    
                    ['or','Odia (Oriya)'],['ps','Pashto'],['fa','Persian'],['pl','Polish'],['pt','Portuguese'],                    
                    ['pa','Punjabi'],['ro','Romanian'],['ru','Russian'],['sm','Samoan'],['gd','Scots Gaelic'],                    
                    ['sr','Serbian'],['st','Sesotho'],['sn','Shona'],['sd','Sindhi'],['si','Sinhala'],                    
                    ['sk','Slovak'],['sl','Slovenian'],['so','Somali'],['es','Spanish'],['su','Sundanese'],                    
                    ['sw','Swahili'],['sv','Swedish'],['tg','Tajik'],['ta','Tamil'],['tt','Tatar'],                    
                    ['te','Telugu'],['th','Thai'],['tr','Turkish'],['tk','Turkmen'],['ug','Uyghur'],                    
                    ['uk','Ukrainian'],['ur','Urdu'],['uz','Uzbek'],['vi','Vietnamese'],['cy','Welsh'],                    
                    ['xh','Xhosa'],['yi','Yiddish'],['yo','Yoruba'],['zu','Zulu'],['ak','Akan'],                    
                    ['bho','Bhojpuri'],['doi','Dogri'],['dv','Dhivehi'],['ee','Ewe'],['gn','Guarani'],                    
                    ['ilo','Ilocano'],['kri','Krio'],['ckb','Kurdish (Sorani)'],['lg','Luganda'],['mai','Maithili'],                    
                    ['lus','Mizo'],['om','Oromo'],['qu','Quechua'],['sa','Sanskrit'],['nso','Sepedi'],['ti','Tigrinya'],['ts','Tsonga'],
                  ].map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>
                  Adds translation lookups per document — larger batches will take noticeably longer to generate.
                </div>
              </>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={showAmountInWords} onChange={e => setShowAmountInWords(e.target.checked)} />
            Show "Amount in Words" below the total on each document
          </label>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStage('editing')} style={secondaryBtn}><ArrowLeft size={14} /> Back to editing</button>
            {includeQr ? (
              <button onClick={() => setStage('position')} disabled={invoiceGroups.length === 0} style={{ ...primaryBtn, opacity: invoiceGroups.length === 0 ? 0.5 : 1 }}>
                Next: Position QR <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={handleGenerate} disabled={invoiceGroups.length === 0} style={{ ...primaryBtn, opacity: invoiceGroups.length === 0 ? 0.5 : 1 }}>
                <FileText size={14} /> Generate {invoiceGroups.length} document{invoiceGroups.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 'position' && (
        <div style={card}>
          <h3 style={h3}>5. Position the QR code</h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeQr} onChange={e => setIncludeQr(e.target.checked)} />
            Include a "Scan to view invoice" QR code on each document
          </label>

          {includeQr ? (
            <>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: -6, marginBottom: 6 }}>
                Choose where the "Scan to view invoice" QR sits on each document. Its vertical position is
                calculated per-row from that row's own content — so short and long receipts both get the
                QR placed correctly, with no overlap.
              </p>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {(['left', 'center', 'right'] as const).map(a => (
                  <button
                    key={a}
                    onClick={() => { setQrAlign(a); setQrPosition(null); }}
                    style={{
                      padding: isMobileBatchEmbed
  ? '10px 12px'
  : '6px 14px',
flex: isMobileBatchEmbed ? 1 : undefined, borderRadius: 6, border: '1px solid',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: qrAlign === a && !qrPosition ? '#3b82f6' : '#fff',
                      color: qrAlign === a && !qrPosition ? '#fff' : '#374151',
                      borderColor: qrAlign === a && !qrPosition ? '#3b82f6' : '#e5e7eb',
                      textTransform: 'capitalize',
                    }}
                  >{a}</button>
                ))}
              </div>
              {previewHtml ? (
                <QrPositionPreview
  mobile={isMobileBatchEmbed}
              html={previewHtml}
              pageWidth={previewDims.width}
              pageHeight={previewDims.height}
              qrSize={qrSizeForTemplate}
              qrPosition={qrPosition}
              qrAlign={qrAlign}
              onChange={setQrPosition}
            />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 20, color: '#9ca3af', fontSize: 12 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Building preview...
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 14 }}>
              No QR code will be added — documents will generate without one.
            </div>
          )}

        

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => setStage('review')} style={secondaryBtn}><ArrowLeft size={14} /> Back</button>
            {includeQr && <button onClick={() => { setQrPosition(null); setQrAlign('left'); }} style={secondaryBtn}>Reset to default</button>}
            <button onClick={handleGenerate} disabled={invoiceGroups.length === 0} style={{ ...primaryBtn, opacity: invoiceGroups.length === 0 ? 0.5 : 1 }}>
              <FileText size={14} /> Generate {invoiceGroups.length} document{invoiceGroups.length !== 1 ? 's' : ''}
            </button>
          </div>
          <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
        </div>
      )}

      {stage === 'generating' && (
        <div style={card}>
          <h3 style={h3}>Generating...</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Loader2 size={18} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#374151' }}>{progress.stage}</span>
          </div>
          <div style={{ background: '#f1f5f9', borderRadius: 99, height: 10, overflow: 'hidden' }}>
            <div style={{
              width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%`,
              background: 'linear-gradient(to right,#3b82f6,#9333ea)', height: '100%', transition: 'width 0.2s ease',
            }} />
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>{progress.current} / {progress.total}</div>
          <button onClick={cancelGeneration} style={{ ...secondaryBtn, marginTop: 14 }}>Cancel</button>
          <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
        </div>
      )}

      {stage === 'done' && (
        <div style={card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle size={40} color="#16a34a" />
            <h3 style={{ ...h3, marginTop: 12 }}>
              {invoiceGroups.length - rowErrors.length} document{invoiceGroups.length - rowErrors.length !== 1 ? 's' : ''} ready
            </h3>
            {rowErrors.length > 0 && (
              <p style={{ fontSize: 12, color: '#dc2626' }}>{rowErrors.length} row{rowErrors.length !== 1 ? 's' : ''} failed — see errors above if you go back.</p>
            )}
            <div
  style={{
    display: 'flex',
    flexDirection: isMobileBatchEmbed
      ? 'column'
      : 'row',
    gap: 8,
    justifyContent: 'center',
    marginTop: 16,
    width: '100%',
  }}
>
              <button
  onClick={downloadResult}
  style={{
    ...primaryBtn,
    width: isMobileBatchEmbed ? '100%' : undefined,
    minHeight: isMobileBatchEmbed ? 48 : undefined,
    justifyContent: 'center',
    fontSize: isMobileBatchEmbed ? 14 : undefined,
  }}
><Download size={14} /> Download merged PDF</button>
              <button
  onClick={resetAll}
  style={{
    ...secondaryBtn,
    width: isMobileBatchEmbed ? '100%' : undefined,
    minHeight: isMobileBatchEmbed ? 46 : undefined,
    justifyContent: 'center',
  }}
>Start new batch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Renders a single CSV row into HTML using TemplatePreviewCanvas ----------
async function renderRowToHtml(
  row: BatchRow,
  template: ActiveTemplate,
  mapping: Record<string, string | null>,
  dynamicFields: DynamicField[],
  genFallback: (fieldKey: string, rowIndex: number) => string,
  currency: string,
  invoiceNumber: string,
  invoiceQrUrl: string,
  qrPositionOverride?: { x: number; y: number } | null,
  customFields: CustomFieldDef[] = [],
  dayFirstFields: Record<string, boolean> = {},
  qrAlign: 'left' | 'center' | 'right' = 'left',
  translateTargetLang: string = '',
  incomingLineItems?: Array<{ id: string; description: string; qty: number; rate: number; taxRate?: number; taxAmount?: number }>,
  incomingTaxLines?: Array<{ label: string; rate: number; amount?: number }>,
  showAmountInWords: boolean = false,
  totalAmountForWords: number = 0,
  currencyDisplayMode: 'symbol' | 'code' = 'symbol',
): Promise<string> {
  const get = (key: string) => {
    const col = mapping[key];
    return col ? (row.raw[col] || '').trim() : '';
  };
  const pc = PAGE_CONFIGS[template.pageSize || 'A4'] || PAGE_CONFIGS.A4;

  const clientName = get('client_name');
  const clientEmail = get('client_email');
  const clientPhone = get('client_phone');
  const clientAddress = get('client_address');
  const clientTaxId = extractTaxIdFromRow(row.raw, mapping['client_tax_id']);
  const fallbackDesc = get('description') || 'Item';
  const fallbackQty = parseFlexibleNumber(get('qty')) || 1;
  const fallbackRate = parseFlexibleNumber(get('rate')) || 0;
  const now = new Date();
  const todayDate = now.toISOString().split('T')[0];
  const rawDueDate = get('due_date');
  const dueDateParsed = rawDueDate ? normalizeDateTime(rawDueDate, dayFirstFields['due_date']) : null;
  
  // No matter what, Invoice Date & Time are ALWAYS the present day/time (today)
  const invoiceDate = todayDate;
  const dueDate = (dueDateParsed?.valid && dueDateParsed.date) ? dueDateParsed.date : '';
  const invoiceTimeParsed = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const notes = get('notes') || '';

  // Initialize lineItems FIRST so isLargeBatch can read its length safely
  const lineItems = (incomingLineItems && incomingLineItems.length > 0)
    ? incomingLineItems
    : [{ id: `batch-${row.rowIndex}`, description: fallbackDesc, qty: fallbackQty, rate: fallbackRate }];

  const dynamicValues: Record<string, string> = {};
  dynamicFields.forEach(f => {
    let val = get(`dyn_${f.key}`);
    if (!val && AUTO_FALLBACK_FIELD_PATTERN.test(f.key)) {
      val = genFallback(f.key, row.rowIndex);
    }
    if (val) dynamicValues[f.key] = val;
  });

  // Custom fields the user defined in the mapping step — matched against
  // {{label}}-style placeholders the same way dynamic fields are.
  customFields.forEach(f => {
    if (!f.column || !f.label.trim()) return;
    const val = (row.raw[f.column] || '').trim();
    if (val) dynamicValues[f.label.toLowerCase().replace(/\s+/g, '_')] = val;
  });

  // isEditMode:true in TemplatePreviewCanvas skips invoice-number and
  // dynamicValues substitution entirely — so we pre-patch a cloned template
  // here instead of relying on that path.
  // If order has many items (> 8), compact table row height so all 20 items + footer fit on 1 page
  const isLargeBatch = lineItems.length > 8;
  const compactRowHeight = isLargeBatch ? Math.max(17, Math.floor(400 / lineItems.length)) : undefined;

  const patchedTemplate: ActiveTemplate = {
    ...template,
    elements: template.elements.map(el => {
      // Assign actual tax lines and compact row height for large item lists
      if (el.type === 'table') {
        return {
          ...el,
          taxes: incomingTaxLines && incomingTaxLines.length > 0
            ? incomingTaxLines.map(t => ({ label: t.label, rate: t.rate, amount: t.amount }))
            : [],
          discounts: [],
          discountRate: 0,
          taxRate: 0,
          rowHeight: compactRowHeight || el.rowHeight,
          fontSize: isLargeBatch ? 9 : el.fontSize,
        };
      }
      // Any QR element that belongs to the TEMPLATE ITSELF (as opposed to
      // the "Scan to view invoice" QR this function injects further below)
      // must never be mistaken for that injected one downstream — whether
      // by this function's own cleanElements filter, or by anything else
      // reading qrSource/id later (e.g. a bilingual renderer). Tag it
      // unambiguously here, once, instead of leaving it to be inferred.
      // Without this, a template whose own QR id happens to fall inside
      // the reserved "invoice-qr-" namespace (e.g. reused from a prior
      // batch output saved as a custom template) gets misidentified as
      // the real invoice QR and wrongly ends up carrying the live invoice
      // URL, while the actual "Scan to view invoice" QR is left unpaired.
      if (el.type === 'qr') {
        const dynamicVal = (el as any).dynamicFieldKey ? dynamicValues[(el as any).dynamicFieldKey] : null;
        el = {
          ...el,
          // Preserve template payment/website URL exactly
          qrValue: dynamicVal || el.qrValue || '',
          qrSource: 'template',
          _auto: undefined, // Clear any _auto tag so it is never treated as the invoice QR
          id: `template-payment-qr-${row.rowIndex}`,
        };
      }

      if ((el as any).dynamicFieldKey && dynamicValues[(el as any).dynamicFieldKey] !== undefined) {
        const value = dynamicValues[(el as any).dynamicFieldKey];
        if (el.type === 'text') return { ...el, textContent: value };
        if (el.type === 'barcode') return { ...el, barcodeValue: value };
      }

      if (el.type !== 'text' || !el.textContent) return el;
      let t = el.textContent;

      // Clear any pre-saved template demo Arabic so it never leaks into the output
      delete (el as any).arabicContent;

      // Replace template demo "Bill To" block with the real client info + GSTIN from CSV
      if (/bill\s*to\s*:?/i.test(t)) {
        const taxLine = clientTaxId ? (clientTaxId.length === 15 ? `GSTIN: ${clientTaxId}` : `Tax ID: ${clientTaxId}`) : '';
        const infoLines = [
          clientName,
          clientAddress,
          taxLine,
          clientEmail,
          clientPhone,
        ].filter(Boolean);
        t = t.replace(/(bill\s*to\s*:?\s*\n?)([\s\S]*)/i, (_, heading: string) => `${heading}${infoLines.join('\n')}`);
      }

      // Clean universal placeholders swap
      t = t
        .replace(/\{invoice_number\}/g, invoiceNumber)
        .replace(/\{date\}/g, invoiceDate || todayDate)
        .replace(/\{client_name\}/g, clientName || '')
        .replace(/\{client_address\}/g, clientAddress || '');

      for (const [key, value] of Object.entries(dynamicValues)) {
        if (!value) continue;
        const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#]*');
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_, l) => `${l}: ${value}`);
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*_{2,}`, 'gi'), (_, l) => `${l}: ${value}`);
        t = t.replace(new RegExp(`(${labelPattern})\\s*:\\s*[-—]{1,3}`, 'gi'), (_, l) => `${l}: ${value}`);
        t = t.replace(
          new RegExp(`(${labelPattern})\\s*:\\s*([^\\n:]{0,50}?)(?=\\s{2,}[A-Za-z]|\\n|$)`, 'gi'),
          (_, l) => `${l}: ${value}`
        );
      }

      // Auto-append Tax ID / GSTIN to the "Bill To" box if mapped
      if (clientTaxId && /bill\s*to\s*:/i.test(t) && !t.includes(clientTaxId)) {
        t = `${t}\nTax ID / GSTIN: ${clientTaxId}`;
      }

      return { ...el, textContent: t };
    }),
  };

  // Force the invoice number onto every document, even if this template has
  // no {invoice_number} placeholder or invoice-labeled text field.
  const invoiceNumberAlreadyShown = patchedTemplate.elements.some(
    (el: any) => el.type === 'text' && typeof el.textContent === 'string' && el.textContent.includes(invoiceNumber)
  );
  if (!invoiceNumberAlreadyShown) {
    const fallbackW = pc.width;
    const fallbackPos = { x: 20, y: 20 };
    patchedTemplate.elements = [
      ...patchedTemplate.elements,
      {
        type: 'text',
        textContent: `Invoice #: ${invoiceNumber}`,
        x: fallbackPos.x, y: fallbackPos.y, width: Math.min(200, fallbackW - fallbackPos.x - 20), height: 20,
        fontSize: 10, textAlign: 'left', color: '#111827', fontWeight: '700', zIndex: 20,
      } as any,
    ];
  }

  const isBilingual = !!translateTargetLang && translateTargetLang !== 'en';
  const translatedQrCaption = isBilingual ? await translateCached('Scan to view invoice', translateTargetLang) : '';

  if (!invoiceQrUrl) {
    // If QR was toggled off, remove any leftover placeholder QRs so they don't print
    patchedTemplate.elements = patchedTemplate.elements.filter(
      (el: any) => !(el.type === 'qr' && /yourapp|yourcompany|example\.com|placeholder/i.test(el.qrValue || ''))
    );
  }

  if (isBilingual) {
    const cleanTemplateElements = patchedTemplate.elements.filter(
      (el: any) =>
        !(el.type === 'qr' && String(el.id || '').startsWith('invoice-qr-')) &&
        !(el.type === 'text' && String(el.id || '').startsWith('invoice-label-'))
    );

    // Collect all element text lines, table headers, and line items for this document
    const docBatchTexts: string[] = ['Scan to view invoice'];
    cleanTemplateElements.forEach((el: any) => {
      if (el.type === 'text' && el.textContent?.trim()) {
        el.textContent.split('\n').forEach((l: string) => l.trim() && docBatchTexts.push(l.trim()));
      }
      if (el.type === 'table') {
        (el.columns || []).forEach((c: any) => c.header && docBatchTexts.push(c.header));
      }
    });
    lineItems.forEach(i => i.description && docBatchTexts.push(i.description));

    // Translate the whole invoice in 1 SINGLE API CALL before rendering
    await preloadBatchTranslations(docBatchTexts, translateTargetLang);

    const translatedTemplateElements = await Promise.all(
      cleanTemplateElements.map(async (el: any) => {
        if (el.type === 'text' && el.textContent?.trim()) {
          const lines = el.textContent.split('\n');
          const translatedLines = await Promise.all(lines.map((l: string) => translateCached(l, translateTargetLang)));
          return { ...el, arabicContent: translatedLines.join('\n') };
        }
        if (el.type === 'table') {
          const translatedCols = await Promise.all(
            (el.columns || []).map(async (col: any) => ({
              ...col,
              arabicHeader: await translateCached(col.header, translateTargetLang),
            }))
          );
          return { ...el, columns: translatedCols };
        }
        return el;
      })
    );

    const hasTaxLinesBilingual = incomingTaxLines && incomingTaxLines.length > 0;
    const translatedLineItems = await Promise.all(
      lineItems.map(async item => ({
        ...item,
        taxRate: hasTaxLinesBilingual ? 0 : ((item as any).taxRate || 0),
        arabicName: await translateCached(item.description, translateTargetLang),
      }))
    );

    // Strip pre-baked (9%) from label so BilingualInvoiceRenderer doesn't double print it
    const cleanedBilingualTaxLines = (incomingTaxLines || []).map(t => ({
      ...t,
      label: t.label.replace(/\s*\([^)]*\)/g, '').replace(/\s*\d+(\.\d+)?%/g, '').trim(),
    }));

    const sym = getCurrencyDisplay(currency, currencyDisplayMode);

    const amountWordsAr = showAmountInWords
      ? await translateCached(amountToWords(totalAmountForWords, currency), translateTargetLang)
      : '';

    const extractedToken = invoiceQrUrl.startsWith('http')
      ? invoiceQrUrl.replace(/^.*\/invoice\/view\//, '')
      : (invoiceQrUrl || invoiceNumber);

    const bilingualTemplateConfig = {
      ...patchedTemplate,
      elements: translatedTemplateElements,
      id: extractedToken,
      publicToken: extractedToken,
      public_token: extractedToken,
    };

    const sanitizedTemplateElements = translatedTemplateElements.map((el: any) => {
      if (el.type === 'qr') {
        return { ...el, _auto: undefined, qrSource: 'template' };
      }
      return el;
    });

    const explicitSavedElements = [
      ...sanitizedTemplateElements,
      ...(invoiceQrUrl ? [
        {
          id: `invoice-qr-${extractedToken}`,
          type: 'qr',
          _auto: 'invoice',
          qrValue: invoiceQrUrl,
          width: 100,
          height: 100,
        },
        {
          id: `invoice-label-${extractedToken}`,
          type: 'text',
          _auto: 'invoice_label',
          textContent: 'Scan to view invoice',
          arabicContent: translatedQrCaption,
        }
      ] : []),
    ];

    const container = document.createElement('div');
    container.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${pc.width}px`;
    document.body.appendChild(container);
    const { createRoot } = await import('react-dom/client');
    const root = createRoot(container);

    await new Promise<void>(resolve => {
      root.render(
        React.createElement(BilingualInvoiceRenderer, {
          template: bilingualTemplateConfig,
          savedElements: explicitSavedElements,
          qrValue: invoiceQrUrl,
          invoiceQRValue: invoiceQrUrl,
          qrCaptionInvoiceAr: translatedQrCaption,
          invoiceNumber: invoiceNumber,
          autoQrPosition: qrAlign,
          showTrackingQR: false,
          clientName, clientEmail, clientPhone, clientAddress,
          invoiceDate, invoiceTime: invoiceTimeParsed, notes,
          lineItems: translatedLineItems,
          taxLines: cleanedBilingualTaxLines, discountLines: [], sym, shippingCost: 0,
          translateTargetLang,
          isBilingual: true,
          bilingualLayout: 'side',
          isThermal: (template.pageSize || '').startsWith('THERMAL_'),
          currencyCode: currency,
          showAmountInWords,
          amountInWordsTranslated: amountWordsAr,
        })
      );
      setTimeout(resolve, 1000);
    });
    const bodyHtml = container.innerHTML;
    root.unmount();
    document.body.removeChild(container);
    return bodyHtml;
  }

  // 1. Let buildCwElements build the dynamic line items & totals box naturally
  const hasTaxLines = incomingTaxLines && incomingTaxLines.length > 0;
  const cwData = buildCwElements(patchedTemplate, {
    lineItems: lineItems.map(i => ({
      ...i,
      taxRate: hasTaxLines ? 0 : ((i as any).taxRate || 0),
    })),
    taxLines: incomingTaxLines || [],
    discountLines: [],
    shippingCost: 0,
    clientName, clientEmail, clientPhone, clientAddress, notes,
    invoiceDate, dueDate, showDueDate: !!dueDate,
    showInvoiceQR: false, autoQrPosition: 'center', autoQrSize: 100, autoQrLabelSize: 8,
    autoQrOffset: { x: 0, y: 0 }, qrValue: '',
  });

  // 1. Inject GSTIN / Tax ID and auto-expand the Bill To box & gray background card
  const billToEl = cwData.cwElements.find((el: any) =>
    el.type === 'text' && (/bill\s*to/i.test(el.textContent || '') || (clientName && (el.textContent || '').includes(clientName)))
  );

  if (billToEl && billToEl.textContent) {
    if (clientTaxId && !billToEl.textContent.includes(clientTaxId)) {
      const taxLabel = clientTaxId.length === 15 ? `GSTIN: ${clientTaxId}` : `Tax ID: ${clientTaxId}`;
      const lines = billToEl.textContent.split('\n');
      const emailOrPhoneIdx = lines.findIndex((l: string) => /@|\+?\d{10,}/.test(l));
      if (emailOrPhoneIdx > 0) {
        lines.splice(emailOrPhoneIdx, 0, taxLabel);
      } else {
        lines.push(taxLabel);
      }
      billToEl.textContent = lines.join('\n');
    }

    // Auto-calculate exact height needed for address, GSTIN, email & phone
    const totalLines = billToEl.textContent.split('\n').length;
    const fontSize = billToEl.fontSize || 9.5;
    const neededTextHeight = Math.max(billToEl.height || 70, (totalLines * (fontSize * 1.55)) + 16);
    billToEl.height = neededTextHeight;

    // Find and auto-expand the gray card shape behind Bill To
    const cardShape = cwData.cwElements.find((el: any) =>
      el.type === 'shape' &&
      Math.abs((el.x || 0) - (billToEl.x || 0)) < 50 &&
      (el.y || 0) <= (billToEl.y || 0) + 5 &&
      ((el.y || 0) + (el.height || 0)) >= (billToEl.y || 0) + 30
    );

    let cardBottom = (billToEl.y || 0) + billToEl.height;
    if (cardShape) {
      cardShape.height = Math.max(cardShape.height || 70, neededTextHeight + 20);
      cardBottom = (cardShape.y || 0) + cardShape.height;
    }

    // Push table and items downward to maintain clean spacing
    cwData.cwElements.forEach((el: any) => {
      if (el !== billToEl && el !== cardShape && (el.y || 0) >= (billToEl.y || 0) + 30) {
        if ((el.y || 0) < cardBottom + 18) {
          el.y = Math.round(cardBottom + 18);
        }
      }
    });
  }

  // Ensure table in cwData retains clean tax lines with accurate rates and amounts
  const tableElCw = cwData.cwElements.find((el: any) => el.type === 'table');
  if (tableElCw && incomingTaxLines && incomingTaxLines.length > 0) {
    const computedSubtotal = lineItems.reduce((sum, item) => sum + (item.qty * item.rate), 0);
    tableElCw.taxes = incomingTaxLines.map((t: any) => ({
      label: t.label,
      rate: t.rate || (computedSubtotal > 0 && t.amount ? +((t.amount / computedSubtotal) * 100).toFixed(4) : 0),
      amount: t.amount,
      hideRate: true,
    }));
  }

  // 2. Insert "Amount in Words" centered right below the table totals, pushing footer down
  if (showAmountInWords) {
    const tableElAfterBuild: any = cwData.cwElements.find((el: any) => el.type === 'table');
    if (tableElAfterBuild) {
      const amountWords = amountToWords(totalAmountForWords, currency);
      const tableBottomAfterBuild = (tableElAfterBuild.y || 0) + (tableElAfterBuild.height || 0);
      const totalsClearance = 16;
      const wordsHeight = 24;

      cwData.cwElements.forEach((el: any) => {
        if (el.type === 'table') return;
        if ((el.y || 0) >= (tableElAfterBuild.y || 0)) {
          el.y = (el.y || 0) + totalsClearance + wordsHeight;
        }
      });

      cwData.cwElements.push({
        id: 'batch-amount-in-words',
        type: 'text',
        x: 20,
        y: tableBottomAfterBuild + totalsClearance,
        width: Math.max(100, pc.width - 40),
        height: wordsHeight,
        zIndex: 998,
        opacity: 100,
        rotation: 0,
        textContent: `Amount in Words: ${amountWords}`,
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 9.5,
        fontWeight: '500',
        textAlign: 'center',
        color: '#475569',
      });
    }
  }

  // 3. Inject "Scan to view invoice" QR code (positioned on bottom-right matching Image 2)
  if (invoiceQrUrl) {
    const isThermalTpl = (template.pageSize || '').startsWith('THERMAL_');
    const pageW = isThermalTpl ? pc.width : 794;
    const qrSize = isThermalTpl ? 80 : 100;
    const LEFT_MARGIN = 60;

    let qrX: number;
    if (qrPositionOverride) {
      qrX = qrPositionOverride.x;
    } else {
      qrX = qrAlign === 'left' ? LEFT_MARGIN
        : qrAlign === 'center' ? (pageW - qrSize) / 2
        : pageW - qrSize - LEFT_MARGIN;
    }

    const tableEl = cwData.cwElements.find((el: any) => el.type === 'table');
    const tableBottom = tableEl ? (tableEl.y || 0) + (tableEl.height || 120) : 0;

    // Calculate real bottom including multiline Notes & Terms
    const maxContentY = cwData.cwElements.reduce((max: number, el: any) => {
      if (el.type === 'shape' && (el.height || 0) >= (pc.height || 1123) * 0.7) return max;
      let elHeight = el.height || 30;
      if (el.type === 'text' && el.textContent) {
        const lines = el.textContent.split('\n');
        const approxCharsPerLine = Math.max(20, Math.floor((el.width || 300) / 6.5));
        const totalLines = lines.reduce((acc: number, line: string) => {
          return acc + Math.max(1, Math.ceil(line.length / approxCharsPerLine));
        }, 0);
        elHeight = Math.max(elHeight, totalLines * ((el.fontSize || 9.5) * 1.45 + 3));
      }
      return Math.max(max, (el.y || 0) + elHeight);
    }, tableBottom);

    // Place QR code strictly beneath the last note
    const qrY = qrPositionOverride
      ? qrPositionOverride.y
      : maxContentY + 24;

    const labelY = qrY + qrSize + 4;
    const labelWidth = Math.max(qrSize + 40, 140);
    const labelX = Math.max(0, qrX + (qrSize - labelWidth) / 2);

    cwData.cwElements.push(
      {
        id: `invoice-qr-${row.rowIndex}`,
        type: 'qr',
        _auto: 'invoice',
        qrSource: 'invoice',
        qrValue: invoiceQrUrl,
        x: qrX,
        y: qrY,
        width: qrSize,
        height: qrSize,
        zIndex: 99,
        color: '#000000',
      } as any,
      {
        id: `invoice-label-${row.rowIndex}`,
        type: 'text',
        _auto: 'invoice_label',
        textContent: 'Scan to view invoice',
        x: labelX,
        y: labelY,
        width: labelWidth,
        height: 18,
        fontSize: 9,
        fontFamily: 'Inter, sans-serif',
        textAlign: 'center',
        color: '#374151',
        fontWeight: '700',
        zIndex: 99,
      } as any
    );
  }

  const pageBehavior = 'fixed';

  const html = await renderCwToHtml(
    patchedTemplate, cwData, pageBehavior, pc.width,
    currency, currencyDisplayMode,
    true // bodyOnly — CSS is included once in handleGenerate's fullHtml instead
  );

  // Strip stray rate suffixes added by renderer since percentage is already in the label
  const cleanHtml = html.replace(/\s*\+\d+(?:\.\d+)?\s*%/g, '');
  return cleanHtml;
}





// ---------- small UI helpers ----------
function StageHeader({ stage, mobile = false }: { stage: Stage; mobile?: boolean }) {
  const steps: { key: Stage; label: string; short: string }[] = [
    { key: 'template', label: 'Template', short: '1' },
    { key: 'upload', label: 'Upload', short: '2' },
    { key: 'mapping', label: 'Map columns', short: '3' },
    { key: 'editing', label: 'Edit rows', short: '4' },
    { key: 'review', label: 'Review', short: '5' },
    { key: 'position', label: 'Position QR', short: '6' },
    { key: 'done', label: 'Done', short: '7' },
  ];
  const activeIdx = steps.findIndex(s => s.key === stage || (stage === 'generating' && s.key === 'review'));
  const circleSize = mobile ? 22 : 26;

  return (
    <div
      style={{
        marginBottom: 22,
        overflowX: mobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: mobile ? 4 : 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: mobile ? steps.length * 74 : undefined }}>
        {steps.map((s, i) => {
          const isDone = i < activeIdx;
          const isActive = i === activeIdx;
          const dotColor = isDone ? '#16a34a' : isActive ? '#3b82f6' : '#e2e8f0';
          const textColor = isDone ? '#16a34a' : isActive ? '#1e293b' : '#9ca3af';

          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: mobile ? 74 : undefined, flex: mobile ? undefined : 1 }}>
                <div
                  style={{
                    width: circleSize, height: circleSize, borderRadius: '50%',
                    background: isDone || isActive ? dotColor : '#fff',
                    border: `2px solid ${dotColor}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: mobile ? 10 : 11, fontWeight: 800,
                    color: isDone || isActive ? '#fff' : '#9ca3af',
                    flexShrink: 0, transition: 'all 0.2s ease',
                  }}
                >
                  {isDone ? '✓' : s.short}
                </div>
                <div
                  style={{
                    marginTop: 6, fontSize: mobile ? 9.5 : 11, fontWeight: isActive ? 700 : 600,
                    color: textColor, textAlign: 'center', lineHeight: 1.25,
                    maxWidth: mobile ? 68 : 90,
                  }}
                >
                  {s.label}
                </div>
              </div>
              {i < steps.length - 1 && (
                <div
                  style={{
                    flex: mobile ? '0 0 20px' : 1,
                    height: 2,
                    background: i < activeIdx ? '#16a34a' : '#e2e8f0',
                    marginTop: circleSize / 2,
                    transition: 'background 0.2s ease',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function QrPositionPreview({
  html,
  pageWidth,
  pageHeight,
  qrSize,
  qrPosition,
  onChange,
  alignToQr,
  markerWidth,
  markerHeight,
  markerLabel,
  mobile = false,
  qrAlign = 'left',
}: {
  html: string;
  pageWidth: number;
  pageHeight: number;
  qrSize: number;
  qrPosition: { x: number; y: number } | null;
  onChange: (pos: { x: number; y: number }) => void;
  alignToQr?: { x: number; y: number; width: number } | null;
  markerWidth?: number;
  markerHeight?: number;
  markerLabel?: string;
  mobile?: boolean;
  qrAlign?: 'left' | 'center' | 'right';
}) {
  const boxW = markerWidth ?? qrSize;
  const boxH = markerHeight ?? qrSize;
  const displayWidth = mobile
  ? Math.min(340, Math.max(260, window.innerWidth - 48))
  : 380;
  const scale = displayWidth / pageWidth;
  const displayHeight = pageHeight * scale;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Actual rendered content height (unscaled px) — measured from the
  // iframe's real document once it loads. Falls back to pageHeight until
  // then. Batches with many line items render taller than one A4 page, and
  // the box was previously hard-capped to exactly `displayHeight`
  // (= pageHeight * scale) with overflow:hidden — anything past that was
  // silently clipped with no way to scroll down and see it.
  const [measuredContentHeight, setMeasuredContentHeight] = useState(pageHeight);

  useEffect(() => {
    setMeasuredContentHeight(pageHeight);
  }, [html, pageHeight]);

  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc?.body) {
        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, pageHeight);
        setMeasuredContentHeight(h);
      }
    } catch {
      // sandboxed iframe — if this ever throws, just keep the pageHeight fallback
    }
  };

  // Fixed bottom-left default, matching the placement used at generation
  // time (renderRowToHtml) — so the marker's untouched position is exactly
  // where the QR will land in the final PDF.
  const LEFT_MARGIN = 60;

  const defaultX =
    qrAlign === 'left'
      ? LEFT_MARGIN
      : qrAlign === 'right'
        ? pageWidth - boxW - LEFT_MARGIN
        : (pageWidth - boxW) / 2;

  // Boundary for QR dragging stays based on the real (possibly multi-page)
  // content height, clamped so the box can never be dragged off the bottom
  // of whatever content actually exists.
  const clampBoundH = Math.max(measuredContentHeight, pageHeight);

  const defaultPos = {
    x: defaultX,
    y: Math.max(0, pageHeight - boxH - 60),
  };

  const pos = qrPosition || defaultPos;

  const clampAndSet = (clientX: number, clientY: number) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let x = (clientX - rect.left) / scale - boxW / 2;
    let y = (clientY - rect.top) / scale - boxH / 2;
    x = Math.max(0, Math.min(pageWidth - boxW, x));
    y = Math.max(0, Math.min(clampBoundH - boxH, y));
    onChange({ x, y });
  };

  const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;}</style></head><body>${html}</body></html>`;

  const canvasDisplayHeight = clampBoundH * scale;

  return (
    <div
      ref={wrapRef}
      style={{
        width: displayWidth,
        maxHeight: mobile ? 460 : 560,
        overflowY: 'auto',
        overflowX: 'hidden',
        margin: '0 auto 16px', border: '1px solid #e5e7eb', borderRadius: 8,
        background: '#fff',
      }}
    >
      <div
        ref={canvasRef}
        onPointerMove={(e) => { if (draggingRef.current) clampAndSet(e.clientX, e.clientY); }}
        onPointerUp={() => { draggingRef.current = false; }}
        onPointerLeave={() => { draggingRef.current = false; }}
        style={{
          position: 'relative', width: displayWidth, height: canvasDisplayHeight,
          background: '#fff', touchAction: 'none',
        }}
      >
      <iframe
        ref={iframeRef}
        onLoad={handleIframeLoad}
        srcDoc={docHtml}
        title="qr-position-preview"
        sandbox=""
        style={{
          width: pageWidth, height: clampBoundH, border: 'none',
          transform: `scale(${scale})`, transformOrigin: 'top left',
          pointerEvents: 'none', display: 'block',
        }}
      />
      <div
        onPointerDown={(e) => {
          e.preventDefault();
          draggingRef.current = true;
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
        }}
        style={{
          position: 'absolute',
          left: pos.x * scale, top: pos.y * scale,
          width: boxW * scale, height: boxH * scale,
          border: '2px dashed #3b82f6', background: 'rgba(59,130,246,0.15)',
          borderRadius: 4, cursor: 'grab', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#1d4ed8',
          userSelect: 'none',
        }}
      >
        {markerLabel || 'QR'}
      </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color = '#111827' }: { label: string; value: number; color?: string }) {
  return (
    <div style={{ flex: 1, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
}

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, position: 'relative' };
const h3: React.CSSProperties = { margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#111827' };
const selectStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, background: '#fff' };
const dropZone: React.CSSProperties = { position: 'relative', border: '2px dashed #d1d5db', borderRadius: 10, padding: '36px 20px', textAlign: 'center', cursor: 'pointer', background: '#fafafa' };
const primaryBtn: React.CSSProperties = { marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const secondaryBtn: React.CSSProperties = { marginTop: 14, marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
